import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "./ManageVendors.css";
import { showToast } from "../../utils/toast";
import { handleRawFetch401 } from "../../utils/apiInterceptor";
import { FiEdit3 } from "react-icons/fi";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaListUl,
  FaListOl,
  FaImage,
  FaLink,
  FaSearch,
  FaPlus,
  FaEllipsisV,
  FaEdit,
  FaTrash,
  FaCloudUploadAlt,
  FaQuestionCircle,
  FaCameraRetro,
  FaArrowLeft,
  FaInfoCircle,
  FaFilter,
  FaEye,
  FaCog,
  FaShopify,
  FaEyeSlash,
  FaSync,
  FaCopy,
  FaExternalLinkAlt,
  FaDownload,
  FaUpload
} from "react-icons/fa";
import Pagination from "../ShipturtleProducts/Pagination";
import BulkUpdateVendorsPage from "./BulkUpdateVendorsPage";
import ExportVendorsModal from "./ExportVendorsModal";
import { useTaraTerminalFocus } from "../../utils/taraTerminalContext";
import DataFallback from "../feedback/DataFallback";
import { classifyError } from "../../utils/errorTaxonomy";
import {
  getCurrentUserRole,
  exportVendors,
  saveBlobAs,
  updateVendor,
  updateVendorBilling,
  listVendorPickupAddresses,
  createVendorPickupAddress,
  updateVendorPickupAddress,
  deleteVendorPickupAddress,
  listVendorContacts,
  createVendorContact,
  updateVendorContact,
  deleteVendorContact,
  listProducts,
  getSyncFieldFilters,
  updateSyncFieldFilters,
  getShopifyVendorConfig,
  getActiveWebhooks,
  deregisterWebhook,
  deregisterWebhookByType
} from "../../services/shipturtleApi";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/shipturtle/vendors`;

// List of available shipper companies
const SHIPPER_COMPANIES = [
  "Hand Delivered",
  "Delhivery",
  "BlueDart",
  "Shiprocket",
  "Amazon Warehouse Fulfillment",
  "Prozo",
  "Nz Post",
  "FedEx",
  "DHL",
  "UPS",
  "DTDC",
  "Ecom Express",
  "Xpressbees",
  "Shadowfax",
  "Pickrr",
];

const getAuthHeaders = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

function ManageVendors() {
  const [vendors, setVendors] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("approved");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userVendorId, setUserVendorId] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [serverPagination, setServerPagination] = useState(null);

  // Tab counts state
  const [approvedCount, setApprovedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  // Selection state. Two modes: either `selectedVendors` holds explicitly ticked ids
  // (which persist across pages), or `selectAllMode` means "everything matching the
  // current filters", resolved server-side, with `excludedIds` unticked afterwards.
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [excludedIds, setExcludedIds] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);

  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showEditVendor, setShowEditVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [editingVendorTab, setEditingVendorTab] = useState('company-details');
  // Vendors get their own profile instead of this list.
  useTaraTerminalFocus('vendors_list', userRole === 'admin' ? {
    tab: activeTab === 'pending' ? 'pending approval' : activeTab,
    search: searchTerm || undefined,
  } : null);

  const { vendorId: urlVendorId } = useParams();
  const [searchParams] = useSearchParams();
  const queryVendorId = searchParams.get('vendor_id') || searchParams.get('vendorId');
  const targetVendorId = urlVendorId || queryVendorId;

  useEffect(() => {
    const tabParam = searchParams.get('tab');

    // If we have a vendorId (from URL or query) and we are admin, fetch it and open edit view
    if (targetVendorId && userRole === 'admin') {
      const fetchAndOpenVendor = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/${targetVendorId}/`, {
            method: "GET",
            headers: getAuthHeaders(),
          });
          if (response.ok) {
            const data = await response.json();
            setEditingVendor(data);
            if (tabParam) setEditingVendorTab(tabParam);
            setShowEditVendor(true);
          }
        } catch (err) {
          console.error("Failed to auto-load vendor from URL/Query:", err);
        }
      };
      fetchAndOpenVendor();
    }
  }, [targetVendorId, userRole, searchParams]);



  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [selectedVendorForWarehouse, setSelectedVendorForWarehouse] = useState(null);

  const [showSuccessPopup, setShowSuccessPopup] = useState({
    created: false,
    approved: false,
    updated: false,
    warehouse: false,
  });

  const [newVendor, setNewVendor] = useState({
    company_name: "",
    tally_name: "",
    brand_name: "",
    primary_contact_name: "",
    primary_contact_phone: "",
    email: "",
    website: "",
    address_city: "",
    public_description: "",
  });

  const [warehouseData, setWarehouseData] = useState({
    name: "",
    contact_person: "",
    contact_number: "",
    contact_email: "",
    gstin: "",
    is_default: false,
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    phone: "",
    landmark: "",
  });

  const closeWarehouseModal = () => {
    setShowWarehouseModal(false);
    setSelectedVendorForWarehouse(null);
    setWarehouseData({
      name: "",
      contact_person: "",
      contact_number: "",
      contact_email: "",
      gstin: "",
      is_default: false,
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      phone: "",
      landmark: "",
    });
  };

  // --- FETCH SINGLE VENDOR DETAIL (for vendor role) ---
  const [vendorDetail, setVendorDetail] = useState(null);
  const fetchVendorDetail = useCallback(async () => {
    if (!userRole || userRole !== "vendor" || !userVendorId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/${userVendorId}/`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleRawFetch401();
          return;
        }
        if (response.status === 404) {
          setError("Vendor not found.");
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.error || errorData.detail || errorData.message || response.statusText;
        setError(`Failed to load vendor details (${response.status}): ${message}`);
        return;
      }

      const data = await response.json();
      setVendorDetail(data);
    } catch (err) {
      console.error("Error fetching vendor detail:", err);
      setError(`Failed to load vendor details: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [userRole, userVendorId]);

  // --- FETCH VENDORS (with server-side pagination) ---
  // The filters the list is currently showing. Shared with the export so a
  // "select all" resolves to exactly these rows rather than a set that drifted.
  const buildBackendFilters = useCallback(() => {
    const filters = {};
    if (activeTab === "approved") filters.status = "approved";
    else if (activeTab === "pending") filters.status = "pending";
    if (searchTerm.trim()) filters.search = searchTerm.trim();
    return filters;
  }, [activeTab, searchTerm]);

  const filterSummary = useMemo(() => {
    const parts = [];
    if (activeTab === "approved") parts.push("Approved");
    else if (activeTab === "pending") parts.push("Pending");
    if (searchTerm.trim()) parts.push(`search "${searchTerm.trim()}"`);
    return parts.join(", ");
  }, [activeTab, searchTerm]);

  const fetchVendors = useCallback(async () => {
    if (!userRole || userRole !== "admin") {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: itemsPerPage.toString(),
      });
      Object.entries(buildBackendFilters()).forEach(([key, value]) => {
        params.append(key, value);
      });

      const response = await fetch(`${API_BASE_URL}/?${params.toString()}`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleRawFetch401();
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.detail || errorData.message || response.statusText;
        setError(`Failed to load vendors (${response.status}): ${message}`);
        return;
      }

      const data = await response.json();

      let vendorsData = [];
      if (data) {
        if (Array.isArray(data)) {
          vendorsData = data;
        } else if (Array.isArray(data.results)) {
          vendorsData = data.results;
        } else if (Array.isArray(data.data)) {
          vendorsData = data.data;
        }
      }

      setAllVendors(vendorsData);

      // Set pagination metadata if available
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        if (data.count !== undefined || data.total_pages !== undefined) {
          setServerPagination({
            total_count: data.count || vendorsData.length,
            total_pages: data.total_pages || Math.ceil((data.count || vendorsData.length) / itemsPerPage),
            current_page: data.current_page || currentPage,
            page_size: itemsPerPage,
            has_next: data.has_next || false,
            has_previous: data.has_previous || false,
          });
        } else {
          setServerPagination(null);
        }
      } else {
        setServerPagination(null);
      }
    } catch (err) {
      console.error("Error fetching vendors:", err);
      setError(`Failed to load vendors: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [userRole, currentPage, itemsPerPage, buildBackendFilters]);

  // Fetch counts for tabs using the single counts API endpoint
  const fetchTabCounts = useCallback(async () => {
    if (!userRole || userRole !== "admin") return;

    try {
      const response = await fetch(`${API_BASE_URL}/counts/`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleRawFetch401();
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data && data.success && data.status_counts) {
        const statusCounts = data.status_counts;

        // Extract counts for approved and pending statuses
        if (statusCounts.approved && statusCounts.approved.count !== undefined) {
          setApprovedCount(statusCounts.approved.count);
        }
        if (statusCounts.pending && statusCounts.pending.count !== undefined) {
          setPendingCount(statusCounts.pending.count);
        }
      }
    } catch (err) {
      console.error("Error fetching vendor counts:", err);
    }
  }, [userRole]);

  useEffect(() => {
    let isMounted = true;

    const fetchRole = async () => {
      try {
        const roleData = await getCurrentUserRole();
        if (!isMounted) return;
        setUserRole(roleData?.role || "vendor");
        setUserVendorId(roleData?.vendor_id || null);
      } catch (error) {
        console.error("Error fetching user role:", error);
        if (isMounted) {
          setUserRole("vendor");
          setUserVendorId(null);
        }
      } finally {
        if (isMounted) {
          setRoleLoading(false);
        }
      }
    };

    fetchRole();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!roleLoading) {
      if (userRole === "vendor") {
        fetchVendorDetail();
      } else if (userRole === "admin") {
        fetchVendors();
      } else {
        // If not admin/vendor, stop loading (they likely have no data access)
        setLoading(false);
      }
    }
  }, [roleLoading, userRole, fetchVendors, fetchVendorDetail]);

  // Fetch tab counts only once when role is loaded (similar to orders module)
  // This matches the pattern in ManageOrders where counts are fetched once on mount
  useEffect(() => {
    if (userRole === "admin" && !roleLoading) {
      fetchTabCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, roleLoading]); // Only depend on userRole and roleLoading, not fetchTabCounts

  // Reset to page 1 when the filters move. Selection is cleared by its own effect
  // below, keyed only on the filters — this one must not depend on userRole /
  // roleLoading, or an unrelated re-render wipes a selection mid-flow.
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  // For admin view, vendors are already filtered by the API
  // No need for client-side filtering when using server-side pagination
  useEffect(() => {
    if (userRole === "admin") {
      setVendors(allVendors);
    }
  }, [allVendors, userRole]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Vendor selection handlers
  const handleVendorSelect = useCallback((vendorId, isSelected) => {
    if (selectAllMode) {
      setExcludedIds(prev =>
        isSelected ? prev.filter(id => id !== vendorId) : (prev.includes(vendorId) ? prev : [...prev, vendorId])
      );
      return;
    }
    setSelectedVendors(prev => {
      if (isSelected) {
        return prev.includes(vendorId) ? prev : [...prev, vendorId];
      } else {
        return prev.filter(id => id !== vendorId);
      }
    });
  }, [selectAllMode]);

  const handleSelectAll = useCallback((isSelected) => {
    const currentPageIds = vendors.map(vendor => vendor.id);

    // In select-all mode the page checkbox adds/removes exclusions rather than
    // editing an id list that mode does not consult.
    if (selectAllMode) {
      setExcludedIds(prev => (isSelected
        ? prev.filter(id => !currentPageIds.includes(id))
        : [...new Set([...prev, ...currentPageIds])]));
      return;
    }

    if (isSelected) {
      // Merge, don't replace — selections persist across pages.
      setSelectedVendors(prev => [...new Set([...prev, ...currentPageIds])]);
    } else {
      setSelectedVendors(prev => prev.filter(id => !currentPageIds.includes(id)));
    }
  }, [vendors, selectAllMode]);

  // Select every vendor matching the current filters, not just the visible page.
  // The set is resolved server-side at action time from the same filters the list
  // used, so it cannot drift from what the admin is looking at.
  const handleSelectAllFiltered = useCallback(() => {
    setSelectAllMode(true);
    setExcludedIds([]);
    setSelectedVendors([]);
  }, []);

  const handleUnselectAll = useCallback(() => {
    setSelectAllMode(false);
    setExcludedIds([]);
    setSelectedVendors([]);
  }, []);

  // Clear the selection whenever the filters move: "all 553" would otherwise
  // silently come to mean a different 553 after a tab or search change. Export does
  // NOT clear the selection — you often want to export core, then all details.
  useEffect(() => {
    setSelectAllMode(false);
    setExcludedIds([]);
    setSelectedVendors([]);
  }, [activeTab, searchTerm]);

  // Calculate pagination values similar to ManageProducts
  const { totalPages, startIndex, endIndex, totalCount } = useMemo(() => {
    if (serverPagination) {
      const pageSize = Number(serverPagination.page_size) || itemsPerPage;
      const totalPagesCalc = Number(serverPagination.total_pages) || 1;
      const start = (currentPage - 1) * pageSize;
      const end = start + (vendors ? vendors.length : 0);
      return {
        totalPages: totalPagesCalc,
        startIndex: start,
        endIndex: end,
        totalCount: Number(serverPagination.total_count) || (vendors ? vendors.length : 0)
      };
    }
    // Fallback to client-side calculation
    const total = vendors.length;
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return {
      totalPages: Math.ceil(total / itemsPerPage),
      startIndex: start,
      endIndex: Math.min(end, total),
      totalCount: total
    };
  }, [vendors, currentPage, itemsPerPage, serverPagination]);

  // In select-all mode the selection is "everything matching the filters", so a row
  // is selected unless it was explicitly unticked. That has to drive the checkboxes
  // too, or the banner claims 553 selected while every box renders empty — and the
  // state would appear to vanish when you page forward.
  const isVendorSelected = useCallback(
    (vendorId) => (selectAllMode
      ? !excludedIds.includes(vendorId)
      : selectedVendors.includes(vendorId)),
    [selectAllMode, excludedIds, selectedVendors],
  );

  const effectiveSelectedCount = selectAllMode
    ? Math.max(totalCount - excludedIds.length, 0)
    : selectedVendors.length;
  const hasSelection = effectiveSelectedCount > 0;

  // Export the current selection. Mirrors ManageProducts.handleExportData: the
  // scope is whatever the selection banner says it is, resolved server-side from
  // the same filters the list used.
  const handleExportData = useCallback(async (preset = 'core') => {
    if (!hasSelection || isExporting) return;
    setIsExporting(true);
    try {
      const { blob, filename } = await exportVendors({
        preset,
        vendorIds: selectedVendors,
        selectAll: selectAllMode,
        filters: buildBackendFilters(),
        excludedIds,
      });
      saveBlobAs(blob, filename);
      showToast({
        type: 'success',
        message: `Exported ${effectiveSelectedCount} vendor${effectiveSelectedCount === 1 ? '' : 's'}.`,
      });
      setShowExportModal(false);
    } catch (err) {
      showToast({ type: 'error', message: err.message || 'Export failed.' });
    } finally {
      setIsExporting(false);
    }
  }, [
    hasSelection,
    isExporting,
    selectedVendors,
    selectAllMode,
    buildBackendFilters,
    excludedIds,
    effectiveSelectedCount,
  ]);

  // --- APPROVE VENDOR ---
  const handleApprove = async (vendorId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${vendorId}/approve/`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleRawFetch401();
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}`);
      }

      showPopup("approved");
      fetchVendors();
      // Refresh counts after approving a vendor (status changes)
      if (userRole === "admin") {
        fetchTabCounts();
      }
    } catch (error) {
      console.error("Error approving vendor:", error);
      showToast({ type: 'error', message: `Failed to approve vendor: ${error.message || 'Check logs for details.'}` });
    }
  };
  const handleDeleteVendor = async (vendorId, vendorName) => {
    if (!window.confirm(`Are you sure you want to delete vendor: ${vendorName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/${vendorId}/`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleRawFetch401();
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}`);
      }

      showToast({ type: 'success', message: `Vendor "${vendorName}" deleted successfully.` });
      fetchVendors(); // Refresh the vendor list
      // Refresh counts after deleting a vendor
      if (userRole === "admin") {
        fetchTabCounts();
      }

    } catch (error) {
      console.error("Error deleting vendor:", error);
      showToast({ type: 'error', message: `Failed to delete vendor: ${error.message || 'Check logs for details.'}` });
    }
  };

  // --- CREATE VENDOR ---
  const handleInputChange = (e) => {
    setNewVendor({ ...newVendor, [e.target.name]: e.target.value });
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    const vendorCode = `VND-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    const payload = { ...newVendor, vendor_code: vendorCode };

    try {
      const response = await fetch(`${API_BASE_URL}/create/`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleRawFetch401();
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}`);
      }

      showPopup("created");
      setShowAddVendor(false);
      fetchVendors();
      // Refresh counts after creating a vendor (new pending vendor added)
      if (userRole === "admin") {
        fetchTabCounts();
      }
    } catch (error) {
      console.error("Error creating vendor:", error);
      showToast({ type: 'error', message: `Failed to create vendor: ${error.message || 'Check logs.'}` });
    }
  };

  // --- OPEN VENDOR DETAIL VIEW ---
  const handleEdit = async (vendor, tab = 'company-details') => {
    try {
      const response = await fetch(`${API_BASE_URL}/${vendor.id}/`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleRawFetch401();
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setEditingVendor(data);
      setEditingVendorTab(tab);
      setShowEditVendor(true);
    } catch (err) {
      console.error("Failed to load vendor details:", err);
      showToast({ type: 'error', message: `Failed to load vendor details: ${err.message || 'Check logs.'}` });
    }
  };

  // --- ADD WAREHOUSE MODAL ---
  const openWarehouseModal = (vendor) => {
    setSelectedVendorForWarehouse(vendor);
    setShowWarehouseModal(true);
  };

  const handleWarehouseInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let nextValue = type === 'checkbox' ? checked : value;

    // Keep numeric-only values for phone/pincode fields to avoid stray characters
    if (name === 'contact_number' || name === 'phone' || name === 'pincode') {
      nextValue = nextValue.replace(/\D/g, '');
    }

    setWarehouseData({ ...warehouseData, [name]: nextValue });
  };

  const handleAddWarehouse = async (e) => {
    e.preventDefault();
    if (!selectedVendorForWarehouse) return;
    const vendorId = selectedVendorForWarehouse.id;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/; // Enforce 10 digit mobile numbers
    const optionalPhoneRegex = /^[0-9]{7,15}$/; // For optional address phone
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/i;

    // Basic validations
    if (!warehouseData.name?.trim()) {
      showToast({ type: 'error', message: 'Warehouse name is required.' });
      return;
    }
    if (!warehouseData.contact_person?.trim()) {
      showToast({ type: 'error', message: 'Contact person is required.' });
      return;
    }
    if (!warehouseData.contact_number?.trim()) {
      showToast({ type: 'error', message: 'Contact number is required.' });
      return;
    }
    if (!phoneRegex.test(warehouseData.contact_number.trim())) {
      showToast({ type: 'error', message: 'Contact number must be a 10 digit mobile number.' });
      return;
    }
    if (!warehouseData.contact_email?.trim() || !emailRegex.test(warehouseData.contact_email.trim())) {
      showToast({ type: 'error', message: 'Valid contact email is required.' });
      return;
    }
    if (!warehouseData.gstin?.trim()) {
      showToast({ type: 'error', message: 'GSTIN is required.' });
      return;
    }
    if (!gstinRegex.test(warehouseData.gstin.trim())) {
      showToast({ type: 'error', message: 'GSTIN must be a valid 15 character value.' });
      return;
    }
    if (!warehouseData.address_line1?.trim()) {
      showToast({ type: 'error', message: 'Address line 1 is required.' });
      return;
    }
    if (!warehouseData.city?.trim() || !warehouseData.state?.trim()) {
      showToast({ type: 'error', message: 'City and State are required.' });
      return;
    }
    if (!/^[0-9]{6}$/.test(String(warehouseData.pincode).trim())) {
      showToast({ type: 'error', message: 'Pincode must be exactly 6 digits.' });
      return;
    }

    if (warehouseData.phone?.trim() && !optionalPhoneRegex.test(warehouseData.phone.trim())) {
      showToast({ type: 'error', message: 'Address phone must be 7-15 digits.' });
      return;
    }

    const payload = {
      warehouse_name: warehouseData.name.trim(),
      contact_person: warehouseData.contact_person.trim(),
      contact_number: warehouseData.contact_number.trim(),
      contact_email: warehouseData.contact_email.trim(),
      gstin: warehouseData.gstin.trim(),
      is_default: !!warehouseData.is_default,
      address: {
        line1: warehouseData.address_line1.trim(),
        line2: warehouseData.address_line2?.trim() || '',
        city: warehouseData.city.trim(),
        state: warehouseData.state.trim(),
        pincode: String(warehouseData.pincode).trim(),
        country: warehouseData.country?.trim() || 'India',
        phone: warehouseData.phone?.trim() || warehouseData.contact_number.trim(),
        landmark: warehouseData.landmark?.trim() || '',
      },
    };

    try {
      await createVendorPickupAddress(vendorId, payload);
      showPopup("warehouse");
      closeWarehouseModal();
    } catch (error) {
      console.error("Failed to add warehouse:", error);
      showToast({ type: 'error', message: `Failed to add warehouse: ${error.message || 'Check logs.'}` });
    }
  };

  // --- SHOW POPUPS ---
  const showPopup = (type) => {
    setShowSuccessPopup({
      created: type === "created",
      approved: type === "approved",
      updated: type === "updated",
      warehouse: type === "warehouse",
    });

    setTimeout(() => {
      setShowSuccessPopup({ created: false, approved: false, updated: false, warehouse: false });
    }, 3000);
  };

  // --- BULK EXPORT ---

  // ---------------------------------------------------
  //  📍 VIEW SWITCH: Manage Vendors or Vendor Details
  // ---------------------------------------------------
  if (showBulkUpdate) {
    return (
      <BulkUpdateVendorsPage
        onBack={() => {
          setShowBulkUpdate(false);
          handleUnselectAll();
          fetchVendors();
        }}
      />
    );
  }

  if (showEditVendor && editingVendor) {
    return (
      <VendorDetailView
        vendor={editingVendor}
        initialTab={editingVendorTab}
        onClose={() => {
          setShowEditVendor(false);
          setEditingVendor(null);
        }}
      />
    );
  }

  // Show loading while role is being fetched
  if (roleLoading) {
    return (
      <div className="manageVendorsContainer">
        <div className="vendorsWhiteBox">
          <div className="loadingContainer">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------
  //  📍 VENDOR PROFILE VIEW (for vendor role)
  // ---------------------------------------------------
  if (userRole === "vendor") {
    return (
      <VendorProfileView
        vendor={vendorDetail}
        loading={loading}
        error={error}
        onRetry={fetchVendorDetail}
        onEdit={async () => {
          if (vendorDetail && vendorDetail.id) {
            try {
              const response = await fetch(`${API_BASE_URL}/${vendorDetail.id}/`, {
                method: "GET",
                headers: getAuthHeaders(),
              });
              if (response.ok) {
                const data = await response.json();
                setEditingVendor(data);
                setShowEditVendor(true);
              }
            } catch (err) {
              console.error("Failed to load vendor details:", err);
              showToast({ type: 'error', message: `Failed to load vendor details: ${err.message || 'Check logs.'}` });
            }
          }
        }}
        onDelete={async () => {
          if (vendorDetail && vendorDetail.id) {
            const vendorName = vendorDetail.company_name || vendorDetail.brand_name || 'Unknown Vendor';
            if (!window.confirm(`Are you sure you want to delete vendor: ${vendorName}? This action cannot be undone.`)) {
              return;
            }
            try {
              const response = await fetch(`${API_BASE_URL}/${vendorDetail.id}/`, {
                method: "DELETE",
                headers: getAuthHeaders(),
              });
              if (!response.ok) {
                if (response.status === 401) {
                  showToast({ type: 'error', message: 'Authentication failed. Please log in again.' });
                  return;
                }
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || errorData.message || errorData.detail || `HTTP ${response.status}`);
              }
              showToast({ type: 'success', message: `Vendor "${vendorName}" deleted successfully.` });
              // After deletion, vendor should be logged out or redirected
              window.location.href = '/';
            } catch (error) {
              console.error("Error deleting vendor:", error);
              showToast({ type: 'error', message: `Failed to delete vendor: ${error.message || 'Check logs for details.'}` });
            }
          }
        }}
      />
    );
  }

  // ---------------------------------------------------
  //  📍 DEFAULT VIEW: MANAGE VENDORS TABLE (for admin role)
  // ---------------------------------------------------
  const canAddVendors = userRole === "admin";

  return (
    <div className="manageVendorsContainer">
      <div className="vendorsWhiteBox">
        {/* Header with icons */}
        <div className="manageVendorsHeader">
          <div className="headerLeft">
            <h2>Manage Vendors</h2>
            <button className="helpIconBtn" title="Help">
              <FaQuestionCircle />
            </button>
            <button className="infoIconBtn" title="Information">
              <FaInfoCircle />
            </button>
          </div>
          {canAddVendors && (
            <div className="vendorToolbarActions">
              <button
                className="addVendorBtn vendorExportBtn"
                onClick={() => setShowExportModal(true)}
                disabled={!hasSelection || isExporting}
                title={
                  hasSelection
                    ? "Export the selected vendors to Excel"
                    : "Select a vendor first to take an export"
                }
              >
                <FaDownload /> {isExporting ? 'Exporting…' : 'Export'}
              </button>
              <button
                className="addVendorBtn vendorBulkUpdateBtn"
                onClick={() => setShowBulkUpdate(true)}
                title="Update existing vendors from a spreadsheet"
              >
                <FaUpload /> Import
              </button>
              <button className="addVendorBtn" onClick={() => setShowAddVendor(true)}>
                + Add Vendor
              </button>
            </div>
          )}
        </div>

        {/* Tabs with counts */}
        <div className="vendorTabs">
          <button
            className={`tabBtn ${activeTab === "approved" ? "activeTab" : ""}`}
            onClick={() => setActiveTab("approved")}
          >
            Approved ({approvedCount})
          </button>
          <button
            className={`tabBtn ${activeTab === "pending" ? "activeTab" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            Pending ({pendingCount})
          </button>
        </div>

        {(selectedVendors.length > 0 || selectAllMode) && (
          <div className="selectionBanner">
            <div className="selectionBannerContent">
              <span className="selectionCount">
                {selectAllMode
                  ? (excludedIds.length > 0
                      ? `${effectiveSelectedCount} of ${totalCount} Vendors Selected.`
                      : `All ${totalCount} Vendors Selected.`)
                  : `(${selectedVendors.length}) Vendors Selected.`}
              </span>
              <button className="selectionBannerButton" onClick={handleUnselectAll}>
                Unselect Vendors
              </button>
              {!selectAllMode && totalCount > selectedVendors.length && (
                <>
                  <span className="selectionBannerDivider">/</span>
                  <button className="selectionBannerButton" onClick={handleSelectAllFiltered}>
                    Select All {totalCount} Vendors
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <ExportVendorsModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          selectedCount={effectiveSelectedCount}
          filterSummary={selectAllMode ? filterSummary : ''}
          isExporting={isExporting}
          onExport={(preset) => handleExportData(preset)}
        />

        {/* Search and Filter Bar */}
        <div className="searchFilterBar">
          <div className="searchInputWrapper">
            <FaSearch className="searchIcon" />
            <input
              type="text"
              className="searchInput"
              placeholder="Search Vendors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filterDropdown">
            <FaFilter className="filterIcon" />
            <select className="filterSelect">
              <option>Search Vendor</option>
            </select>
          </div>
        </div>

        {/* Results Summary */}
        {!loading && !error && vendors.length > 0 && (
          <div className="resultsSummary">
            <p>
              Showing {startIndex + 1}-{Math.min(endIndex, totalCount)} of {totalCount} vendors
            </p>
          </div>
        )}

        {/* Vendor Table */}
        {loading ? (
          <div className="loadingContainer">
            <p>Loading vendors...</p>
          </div>
        ) : error ? (
          <div className="errorContainer">
            <p className="errorText">{error}</p>
            <button className="retryButton" onClick={() => fetchVendors()}>
              Retry
            </button>
          </div>
        ) : vendors.length === 0 ? (
          <p className="noDataText">No {activeTab === "approved" ? "approved" : "pending"} vendors found.</p>
        ) : (
          <>
            <div className="vendorTableWrapper">
              <table className="vendorTable">
                <thead>
                  <tr>
                    <th className="checkboxColumn">
                      {(() => {
                        // Reads through isVendorSelected so select-all mode ticks
                        // the header too, on every page.
                        const pageVendorIds = vendors.map(v => v.id);
                        const selectedOnPage = pageVendorIds.filter(isVendorSelected);
                        const isAllSelected = pageVendorIds.length > 0 && selectedOnPage.length === pageVendorIds.length;
                        const isPartiallySelected = selectedOnPage.length > 0 && selectedOnPage.length < pageVendorIds.length;

                        return (
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            ref={input => {
                              if (input) {
                                input.indeterminate = isPartiallySelected;
                              }
                            }}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        );
                      })()}
                    </th>
                    <th>Logo</th>
                    <th>Vendor Name</th>
                    <th>Shopify Sync</th>
                    <th>Contact</th>
                    <th>Product Count</th>
                    <th>Inventory Location</th>
                    <th>Sales (All Time)</th>
                    <th>Vendor Earning (All Time)</th>
                    <th>Pending to Pay (All Time)</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((vendor) => {
                    const vendorInitial = vendor.company_name?.charAt(0)?.toUpperCase() || vendor.brand_name?.charAt(0)?.toUpperCase() || '?';
                    const contactEmail = vendor.email || vendor.primary_contact_email || '';
                    const contactLocation = vendor.address_city ? `${vendor.address_city}, ${vendor.address_country || 'India'}` : '';

                    return (
                      <tr key={vendor.id}>
                        <td className="checkboxColumn">
                          <input
                            type="checkbox"
                            checked={isVendorSelected(vendor.id)}
                            onChange={(e) => handleVendorSelect(vendor.id, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>
                          <div className="logoCircle" style={{
                            background: vendor.logo_color || '#007b83',
                            color: 'white'
                          }}>
                            {vendorInitial}
                          </div>
                        </td>
                        <td>
                          <div
                            className="vendorNameCell clickableVendorName"
                            onClick={() => handleEdit(vendor)}
                            style={{ cursor: 'pointer' }}
                            title="Click to edit vendor"
                          >
                            <div className="vendorName">{vendor.company_name || vendor.brand_name}</div>
                            {vendor.tally_name ? (
                              <div className="vendorTallyName">Tally: {vendor.tally_name}</div>
                            ) : null}
                            {vendor.brand_name && vendor.brand_name !== vendor.company_name}
                          </div>
                        </td>
                        <td>
                          <span className={`syncBadge ${vendor.shopify_sync_status === 'synced' ? 'synced' :
                            vendor.shopify_sync_status === 'failed' ? 'notSynced' : 'notSynced'
                            }`} style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                            <span style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: vendor.shopify_sync_status === 'synced' ? '#10b981' :
                                vendor.shopify_sync_status === 'failed' ? '#ef4444' : '#94a3b8'
                            }}></span>
                            {vendor.shopify_sync_status === 'synced' ? 'Shopify Connected' :
                              vendor.shopify_sync_status === 'failed' ? 'Connection Failed' : 'Not Connected'}
                          </span>
                        </td>
                        <td>
                          <div className="contactCell">
                            {contactEmail && <div className="contactEmail">{contactEmail}</div>}
                            {contactLocation && <div className="contactLocation">{contactLocation}</div>}
                          </div>
                        </td>
                        <td>{vendor.product_count || 0}</td>
                        <td>{vendor.inventory_location || '—'}</td>
                        <td>Rs. {vendor.sales_all_time || 0}</td>
                        <td>Rs. {vendor.vendor_earning_all_time || 0}</td>
                        <td>Rs. {vendor.pending_to_pay_all_time || 0}</td>
                        <td className="actionCell">
                          <div className="actionButtons">
                            {vendor.status === 'pending' ? (
                              <>
                                <button
                                  className="approveBtn"
                                  onClick={() => handleApprove(vendor.id)}
                                  title="Approve Vendor"
                                >
                                  Approve
                                </button>
                                <button className="iconBtn viewIcon" onClick={() => handleEdit(vendor)} title="View">
                                  <FaEye />
                                </button>
                                <button className="iconBtn editIcon" onClick={() => handleEdit(vendor)} title="Edit">
                                  <FiEdit3 />
                                </button>
                                <button className="iconBtn settingsIcon" onClick={() => handleEdit(vendor, 'connect-shopify')} title="Connect Shopify">
                                  <FaCog />
                                </button>
                                <button
                                  className="iconBtn deleteIcon"
                                  title="Delete"
                                  onClick={() => handleDeleteVendor(
                                    vendor.id,
                                    vendor.company_name || vendor.brand_name || 'Unknown Vendor'
                                  )}
                                >
                                  <FaTrash />
                                </button>
                              </>
                            ) : (
                              <>
                                <select className="statusDropdown">
                                  <option>Active</option>
                                  <option>Inactive</option>
                                </select>
                                <button className="iconBtn viewIcon" onClick={() => handleEdit(vendor)} title="View">
                                  <FaEye />
                                </button>
                                <button className="iconBtn editIcon" onClick={() => handleEdit(vendor)} title="Edit">
                                  <FiEdit3 />
                                </button>
                                <button className="iconBtn settingsIcon" onClick={() => handleEdit(vendor, 'connect-shopify')} title="Connect Shopify">
                                  <FaCog />
                                </button>
                                <button
                                  className="iconBtn deleteIcon"
                                  title="Delete"
                                  onClick={() => handleDeleteVendor(
                                    vendor.id,
                                    vendor.company_name || vendor.brand_name || 'Unknown Vendor'
                                  )}
                                >
                                  <FaTrash />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && !error && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      {/* ✅ ADD VENDOR MODAL */}
      {canAddVendors && showAddVendor && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Add Vendor</h3>
            <form className="addVendorForm" onSubmit={handleCreateVendor}>
              <div className="formGrid">
                {Object.keys(newVendor).map((key) => (
                  <div className="formGroup" key={key}>
                    <label>
                      {key.replace(/_/g, " ").toUpperCase()}
                      {['company_name', 'brand_name', 'email'].includes(key) && <span className="required">*</span>}
                    </label>
                    <input name={key} value={newVendor[key]} onChange={handleInputChange} />
                  </div>
                ))}
              </div>
              <div className="formActions">
                <button type="submit" className="createBtn">Create Vendor</button>
                <button type="button" className="cancelBtn" onClick={() => setShowAddVendor(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ ADD WAREHOUSE MODAL */}
      {showWarehouseModal && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Add Warehouse for {selectedVendorForWarehouse?.company_name}</h3>
            <form className="addVendorForm" onSubmit={handleAddWarehouse}>
              <div className="formGrid">
                <div className="formGroup">
                  <label>Warehouse Name <span className="required">*</span></label>
                  <input name="name" value={warehouseData.name} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>Contact Person <span className="required">*</span></label>
                  <input name="contact_person" value={warehouseData.contact_person} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>Contact Number <span className="required">*</span></label>
                  <input name="contact_number" value={warehouseData.contact_number} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>Contact Email <span className="required">*</span></label>
                  <input name="contact_email" value={warehouseData.contact_email} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>GSTIN <span className="required">*</span></label>
                  <input name="gstin" value={warehouseData.gstin} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>Address Line 1 <span className="required">*</span></label>
                  <input name="address_line1" value={warehouseData.address_line1} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>Address Line 2 <span className="required">*</span></label>
                  <input name="address_line2" value={warehouseData.address_line2} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>City <span className="required">*</span></label>
                  <input name="city" value={warehouseData.city} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>State <span className="required">*</span></label>
                  <input name="state" value={warehouseData.state} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>Pincode <span className="required">*</span></label>
                  <input name="pincode" value={warehouseData.pincode} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>Country <span className="required">*</span></label>
                  <input name="country" value={warehouseData.country} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>Address Phone <span className="required">*</span></label>
                  <input name="phone" value={warehouseData.phone} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label>Landmark <span className="required">*</span></label>
                  <input name="landmark" value={warehouseData.landmark} onChange={handleWarehouseInputChange} />
                </div>
                <div className="formGroup">
                  <label className="checkboxLabel">
                    <input
                      type="checkbox"
                      name="is_default"
                      checked={warehouseData.is_default}
                      onChange={handleWarehouseInputChange}
                    />
                    Set as Default
                  </label>
                </div>
              </div>
              <div className="formActions">
                <button type="submit" className="createBtn">Add Warehouse</button>
                <button type="button" className="cancelBtn" onClick={closeWarehouseModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// 📘 Vendor Profile View Component (simplified view for vendor role)
function VendorProfileView({ vendor, loading, error, onRetry, onEdit, onDelete }) {
  const getStatusColor = (status) => {
    const normalizedStatus = (status || "").toLowerCase();
    if (normalizedStatus === "approved") return "#28a745"; // green
    if (normalizedStatus === "pending") return "#ff7a00"; // orange
    return "#6c757d"; // grey for inactive/other
  };

  const getStatusLabel = (status) => {
    const normalizedStatus = (status || "").toLowerCase();
    if (normalizedStatus === "approved") return "Approved";
    if (normalizedStatus === "pending") return "Pending";
    return "Inactive";
  };

  if (loading) {
    return (
      <div className="manageVendorsContainer">
        <div className="vendorsWhiteBox">
          <div className="loadingContainer">
            <p>Loading vendor profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // This view is a single vendor's profile rather than a list inside a frame,
    // so replacing the region is right here — there is no tab bar or filter set
    // to preserve behind it.
    return (
      <div className="manageVendorsContainer">
        <div className="vendorsWhiteBox">
          <DataFallback
            variant="pane"
            error={typeof error === 'string' ? { message: error, retryable: true } : error}
            onRetry={onRetry}
          />
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="manageVendorsContainer">
        <div className="vendorsWhiteBox">
          <p className="noDataText">Vendor profile not found.</p>
        </div>
      </div>
    );
  }

  const statusColor = getStatusColor(vendor.status);
  const statusLabel = getStatusLabel(vendor.status);
  const vendorInitial = vendor.company_name?.charAt(0)?.toUpperCase() || vendor.brand_name?.charAt(0)?.toUpperCase() || '?';

  return (
    <div className="manageVendorsContainer">
      <div className="vendorsWhiteBox">
        {/* Header */}
        <div className="manageVendorsHeader">
          <div className="headerLeft">
            <h2>Vendor Profile</h2>
          </div>
        </div>

        {/* Vendor Overview Card */}
        <div className="vendorProfileCard">
          <div className="vendorProfileHeader">
            <div className="vendorProfileLogo" style={{
              background: vendor.logo_color || '#007b83',
              color: 'white'
            }}>
              {vendorInitial}
            </div>
            <div className="vendorProfileInfo">
              <div className="vendorProfileNameRow">
                <h3 className="vendorProfileName">{vendor.company_name || vendor.brand_name || 'N/A'}</h3>
                <span
                  className="vendorStatusBadge"
                  style={{
                    backgroundColor: statusColor,
                    color: 'white'
                  }}
                >
                  {statusLabel}
                </span>
              </div>
              {vendor.brand_name && vendor.brand_name !== vendor.company_name && (
                <p className="vendorProfileBrandName">{vendor.brand_name}</p>
              )}
              {vendor.vendor_code && (
                <p className="vendorProfileCode">Vendor Code: {vendor.vendor_code}</p>
              )}
              {vendor.tally_name && (
                <p className="vendorProfileCode">Tally Name: {vendor.tally_name}</p>
              )}
            </div>
          </div>

          {/* Vendor Details */}
          <div className="vendorProfileDetails">
            <div className="vendorDetailRow">
              <span className="vendorDetailLabel">Primary Contact:</span>
              <span className="vendorDetailValue">
                {vendor.primary_contact_name || 'N/A'}
              </span>
            </div>
            {vendor.primary_contact_phone && (
              <div className="vendorDetailRow">
                <span className="vendorDetailLabel">Contact Phone:</span>
                <span className="vendorDetailValue">{vendor.primary_contact_phone}</span>
              </div>
            )}
            {vendor.public_description && (
              <div className="vendorDetailRow">
                <span className="vendorDetailLabel">Description:</span>
                <span className="vendorDetailValue">{vendor.public_description}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="vendorProfileActions">
            <button className="vendorProfileEditBtn" onClick={onEdit} title="Edit Vendor">
              <FiEdit3 /> Edit
            </button>
            <button className="vendorProfileDeleteBtn" onClick={onDelete} title="Delete Vendor">
              <FaTrash /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 📘 Vendor Detail View Component (similar to ProductDetailView)
function VendorDetailView({ vendor, onClose, initialTab = 'company-details' }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab);
  useTaraTerminalFocus('open_vendor', {
    vendor_id: vendor?.id,
    vendor: vendor?.brand_name || vendor?.company_name,
    tab: activeTab,
  });
  const [shopifyConnectData, setShopifyConnectData] = useState({
    shopify_shop_name: "",
    shopify_client_id: "",
    shopify_client_secret: "",
    vendor_name: vendor?.company_name || "",
    vendor_id: vendor?.id || ""
  });
  const [hasShopifyConnected, setHasShopifyConnected] = useState(false);
  const [loadingShopifyConfig, setLoadingShopifyConfig] = useState(false);
  const [connectingShopify, setConnectingShopify] = useState(false);
  const [showShopifyClientId, setShowShopifyClientId] = useState(false);
  const [showShopifyClientSecret, setShowShopifyClientSecret] = useState(false);
  const [shopifyWebhookId, setShopifyWebhookId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [paymentProviders, setPaymentProviders] = useState([]);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [newPaymentIntegration, setNewPaymentIntegration] = useState({
    payment_gateway: "Manual",
    payment_method: "",
    nickname: "",
    account_name: "",
    account_number: "",
    bank_name: "",
    sort_code: "",
    other_information: "",
    is_default: false,
  });
  // Upload refs
  const logoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const promotionBannerInputRef = useRef(null);

  const [warehouses, setWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [warehouseSearchTerm, setWarehouseSearchTerm] = useState("");
  const [showAddWarehouseModal, setShowAddWarehouseModal] = useState(false);
  const [editingWarehouseId, setEditingWarehouseId] = useState(null);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [newWarehouse, setNewWarehouse] = useState({
    warehouse_name: "",
    contact_person: "",
    contact_number: "",
    contact_email: "",
    gstin: "",
    is_default: false,
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      phone: "",
      landmark: "",
    },
  });
  const [warehouseErrors, setWarehouseErrors] = useState({});

  // Contact details state (mirrors warehouses UX)
  const [contacts, setContacts] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [editingContactId, setEditingContactId] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactErrors, setContactErrors] = useState({});
  const [newContact, setNewContact] = useState({
    name: "",
    contact_type: "primary",
    email: "",
    phone: "",
    designation: "",
    is_primary: false,
    is_active: true,
    notes: "",
  });

  const [paymentErrors, setPaymentErrors] = useState({});

  const getEmptyWarehouseForm = () => ({
    warehouse_name: "",
    contact_person: "",
    contact_number: "",
    contact_email: "",
    gstin: "",
    is_default: false,
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      phone: "",
      landmark: "",
    },
  });
  const [shippingSubTab, setShippingSubTab] = useState('shipping-profile');
  const [shippingProfiles, setShippingProfiles] = useState({
    setShippingProfileOnApp: false,
    settingUp3rdPartyShippingRates: false,
    hybridShippingFlow: false,
  });
  const [shippingAutomationRules, setShippingAutomationRules] = useState([]);
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [showAddConditionModal, setShowAddConditionModal] = useState(false);
  const [newRule, setNewRule] = useState({
    rule_title: "",
    shipper_assigned: "",
    conditions: []
  });
  const [newCondition, setNewCondition] = useState({
    parameter: "",
    operator: "",
    value: ""
  });
  const [shippingPartners, setShippingPartners] = useState([]);
  const [loadingShippingPartners, setLoadingShippingPartners] = useState(false);
  const [shippingPartnerSearch, setShippingPartnerSearch] = useState("");
  const [shipperPartnerFilter, setShipperPartnerFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddShippingPartnerModal, setShowAddShippingPartnerModal] = useState(false);
  const [selectedShippingPartners, setSelectedShippingPartners] = useState([]);
  const [newShippingPartner, setNewShippingPartner] = useState({
    shipper_company: "",
    nickname: "",
    credentials: {},
  });
  const [shipperCompanySearch, setShipperCompanySearch] = useState("");
  const [showShipperDropdown, setShowShipperDropdown] = useState(false);
  const [customSettingsSubTab, setCustomSettingsSubTab] = useState('product-settings');

  // Products tab state
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [productsCurrentPage, setProductsCurrentPage] = useState(1);
  const [productsItemsPerPage] = useState(10);
  const [productsServerPagination, setProductsServerPagination] = useState(null);
  const [productsSearchTerm, setProductsSearchTerm] = useState("");
  const productsSearchDebounceRef = useRef(null);
  const productsFetchRef = useRef(null);
  const [productsFetchTrigger, setProductsFetchTrigger] = useState(0); // Used to force re-fetch on retry
  const [customSettings, setCustomSettings] = useState({
    // Product Settings
    overrideProductSettings: false,
    autoApproveProduct: false,
    deleteProduct: false,
    controlProductData: false,
    productFields: {
      product_price: { view: true, requiresApproval: true },
      title: { view: true, requiresApproval: true },
      description: { view: true, requiresApproval: true },
      media: { view: true, requiresApproval: true },
    },
    // Order Settings
    overrideOrderRouting: false,
    routeToMerchantWarehouse: false,
    orderRoutingSettings: false,
    requireOrderConfirmation: false,
    correctAllErrors: false,
    generateShippingLabels: false,
    generateInvoices: false,
    // Vendor Sync Settings
    overrideVendorSync: false,
    allowWebsiteSync: false,
    enableOrderSync: false,
    pushOnlyIfPaid: false,
    pushUnsyncedProducts: false,
    overrideCustomerInfo: false,
    // Shipping Settings
    overrideShippingSettings: false,
    allowAddShippingPartners: false,
    allowGenerateShippingLabels: false,
    allowChangeWarehouse: false,
    allowChangeShipmentStatus: false,
    allowEditPackageDimensions: false,
    // Billing and Invoice
    overrideInvoiceSettings: false,
    vendorType: "Marketplace",
    allowGenerateInvoices: false,
    // Commission
    commissionVariable: 0,
    commissionFixed: 0,
    commissionOnDiscounted: false,
    discountSharedByMarketplace: 84.75,
    commissionOnShipping: false,
    commissionOnShippingType: "fixed",
    commissionOnShippingValue: 100,
  });

  const [syncFieldFilters, setSyncFieldFilters] = useState({
    // Product Sync Fields
    title: true,
    description: true,
    product_type: true,
    status: true,
    media: true,
    tags: true,

    // Variant Sync Fields
    variant_name: true,
    sku: true,
    price: true,
    cost_per_item: true,
    compare_price: true,
    quantity: true,
    weight: true,
    barcode: true,
    track_quantity: true,
    continue_selling: false,
    taxable: true,

    // Order Sync Fields
    enable_order_sync: false,
    push_only_if_order_paid: false,
    push_orders_for_unsynced_products: false,
    override_customer_info_with_merchant_details: false,
  });

  const [loadingSyncFilters, setLoadingSyncFilters] = useState(false);
  const [savingSyncFilters, setSavingSyncFilters] = useState(false);
  const [syncSettingsTab, setSyncSettingsTab] = useState('product'); // 'product' or 'variant'
  const [showRemoveSyncModal, setShowRemoveSyncModal] = useState(false);
  const [removingSync, setRemovingSync] = useState(false);

  const [webhooks, setWebhooks] = useState([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [webhooksError, setWebhooksError] = useState(null);
  const [showRemoveWebhookModal, setShowRemoveWebhookModal] = useState(false);
  const [removingWebhook, setRemovingWebhook] = useState(false);
  const [webhookTypeToRemove, setWebhookTypeToRemove] = useState(null);

  const fetchWebhooks = useCallback(async () => {
    if (!vendor?.id || !hasShopifyConnected) return;

    setWebhooksLoading(true);
    setWebhooksError(null);
    try {
      const data = await getActiveWebhooks(vendor.id);
      setWebhooks(data.webhooks || []);
    } catch (err) {
      console.error("Error fetching webhooks:", err);
      // Requirement: If API returns error (502), show: "Failed to load webhooks. Try again."
      setWebhooksError("Failed to load webhooks. Try again.");
    } finally {
      setWebhooksLoading(false);
    }
  }, [vendor?.id, hasShopifyConnected]);

  useEffect(() => {
    if (activeTab === 'connect-shopify' && hasShopifyConnected) {
      fetchWebhooks();
    }
  }, [activeTab, hasShopifyConnected, fetchWebhooks]);

  const fetchSyncFieldFilters = useCallback(async () => {
    if (!vendor?.id) return;
    setLoadingSyncFilters(true);
    try {
      const data = await getSyncFieldFilters(vendor.id);
      console.log("DEBUG: fetchSyncFieldFilters received data:", data);

      // Read from three separate keys based on tab type
      const productFilters = data?.product_sync_settings || {};
      const variantFilters = data?.variant_sync_settings || {};
      const orderFilters = data?.order_sync_settings || {};

      const filters = {
        // Product fields
        title: productFilters.title ?? true,
        description: productFilters.description ?? true,
        product_type: productFilters.product_type ?? true,
        status: productFilters.status ?? true,
        media: productFilters.media ?? true,
        tags: productFilters.tags ?? true,

        // Variant fields
        variant_name: variantFilters.variant_name ?? true,
        sku: variantFilters.sku ?? true,
        price: variantFilters.price ?? true,
        cost_per_item: variantFilters.cost_per_item ?? true,
        compare_price: variantFilters.compare_price ?? true,
        quantity: variantFilters.quantity ?? true,
        weight: variantFilters.weight ?? true,
        barcode: variantFilters.barcode ?? true,
        track_quantity: variantFilters.track_quantity ?? true,
        continue_selling: variantFilters.continue_selling ?? false,
        taxable: variantFilters.taxable ?? true,

        // Order fields
        enable_order_sync: orderFilters.enable_order_sync ?? false,
        push_only_if_order_paid: orderFilters.push_only_if_order_paid ?? false,
        push_orders_for_unsynced_products: orderFilters.push_orders_for_unsynced_products ?? false,
        override_customer_info_with_merchant_details: orderFilters.override_customer_info_with_merchant_details ?? false,
      };

      setSyncFieldFilters(filters);
    } catch (err) {
      console.error("Error fetching sync field filters:", err);
    } finally {
      setLoadingSyncFilters(false);
    }
  }, [vendor?.id]);

  const handleSaveSyncFieldFilters = async () => {
    if (!vendor?.id) return;
    setSavingSyncFilters(true);
    try {
      // Determine settings type based on active tab
      const settingsType = syncSettingsTab === 'variant' ? 'variant'
        : syncSettingsTab === 'order' ? 'order'
          : 'product';
      await updateSyncFieldFilters(vendor.id, syncFieldFilters, {}, settingsType);
      showToast({ type: 'success', message: 'Sync settings saved successfully!' });
    } catch (err) {
      console.error("Error saving sync field filters:", err);
      showToast({ type: 'error', message: 'Failed to save sync settings.' });
    } finally {
      setSavingSyncFilters(false);
    }
  };

  // Auto-save when toggle changes
  const autoSaveSyncFilters = async (fieldKey, newValue) => {
    // Optimistically update local state
    const nextFilters = { ...syncFieldFilters, [fieldKey]: newValue };
    setSyncFieldFilters(nextFilters);

    if (!vendor?.id || savingSyncFilters) return;

    try {
      setSavingSyncFilters(true);
      // Determine settings type based on active tab
      const settingsType = syncSettingsTab === 'variant' ? 'variant'
        : syncSettingsTab === 'order' ? 'order'
          : 'product';
      // Send partial update via PATCH with correct nested key
      await updateSyncFieldFilters(vendor.id, { [fieldKey]: newValue }, {}, settingsType);
    } catch (err) {
      console.error("Error auto-saving sync field filters:", err);
      showToast({ type: 'error', message: 'Failed to auto-save sync settings.' });
      // Revert local state on error
      setSyncFieldFilters(syncFieldFilters);
    } finally {
      setSavingSyncFilters(false);
    }
  };

  const fetchShopifyConfig = useCallback(async () => {
    if (!vendor?.id) return;
    setLoadingShopifyConfig(true);
    try {
      const config = await getShopifyVendorConfig(vendor.id);
      if (config) {
        setShopifyConnectData({
          shopify_shop_name: config.shopify_shop_name || "",
          shopify_client_id: config.shopify_client_id || "",
          shopify_client_secret: config.shopify_client_secret || "",
          vendor_name: config.vendor_name || vendor.company_name || "",
          vendor_id: vendor.id,
        });
        setShopifyWebhookId(config.vendor_settings?.shopify_product_update_webhook_id);
        setHasShopifyConnected(!!config.shopify_shop_name);
      }
    } catch (err) {
      console.error("Error fetching shopify config:", err);
    } finally {
      setLoadingShopifyConfig(false);
    }
  }, [vendor?.id]);

  useEffect(() => {
    // Fetch sync filters OR shopify config as soon as we have a vendor ID
    if (vendor?.id) {
      if (activeTab === 'sync-settings' || activeTab === 'product-sync') {
        fetchSyncFieldFilters();
      } else if (activeTab === 'connect-shopify') {
        fetchShopifyConfig();
      }
    }
  }, [vendor?.id, fetchSyncFieldFilters, fetchShopifyConfig, activeTab]);

  const [companyDetails, setCompanyDetails] = useState({
    company_name: vendor.company_name || "",
    tally_name: vendor.tally_name || "",
    brand_name: vendor.brand_name || "",
    website: vendor.website || "",
    primary_contact_name: vendor.primary_contact_name || "",
    primary_contact_phone: vendor.primary_contact_phone || "",
    status: vendor.status || "pending",
    public_description: vendor.public_description || "",
    logo: vendor.logo || "",
    documents: Array.isArray(vendor.documents) ? (vendor.documents[0] || "") : (vendor.documents || ""),
    vendor_settings: {
      source: vendor.vendor_settings?.source || 'onboarding',
      created_by_user_id: vendor.vendor_settings?.created_by_user_id || '',
      facebook_link: vendor.vendor_settings?.facebook_link || "",
      instagram_link: vendor.vendor_settings?.instagram_link || "",
      tiktok_link: vendor.vendor_settings?.tiktok_link || "",
      twitter_link: vendor.vendor_settings?.twitter_link || "",
      hide_promotion_banner: vendor.vendor_settings?.hide_promotion_banner || false,
    },
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [promotionBannerFile, setPromotionBannerFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(vendor.logo || "");
  const [documentPreview, setDocumentPreview] = useState(
    Array.isArray(vendor.documents) ? (vendor.documents[0] || "") : (vendor.documents || "")
  );
  const [promotionBannerPreview, setPromotionBannerPreview] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  // Cleanup local blob previews
  useEffect(() => {
    return () => {
      if (typeof promotionBannerPreview === 'string' && promotionBannerPreview.startsWith('blob:')) {
        URL.revokeObjectURL(promotionBannerPreview);
      }
      if (typeof logoPreview === 'string' && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
      if (typeof documentPreview === 'string' && documentPreview.startsWith('blob:')) {
        URL.revokeObjectURL(documentPreview);
      }
    };
  }, [promotionBannerPreview, logoPreview, documentPreview]);

  useEffect(() => {
    // used for vendor_settings.created_by_user_id
    let mounted = true;
    (async () => {
      try {
        const roleData = await getCurrentUserRole();
        if (!mounted) return;
        setCurrentUserId(roleData?.user_id || null);
      } catch (e) {
        // non-fatal
        setCurrentUserId(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);
  const [billingErrors, setBillingErrors] = useState({});
  const [billingData, setBillingData] = useState({
    billing_cycle: vendor.billing?.billing_cycle || vendor.billing_cycle || 'monthly',
    payment_terms: vendor.billing?.payment_terms || vendor.payment_terms || 'net_30',
    billing_email: vendor.billing?.billing_email || vendor.billing_email || '',
    cc_emails: Array.isArray(vendor.billing?.cc_emails)
      ? vendor.billing.cc_emails
      : Array.isArray(vendor.cc_emails)
        ? vendor.cc_emails
        : [],
    currency: vendor.billing?.currency || vendor.currency || 'INR',
    tax_id: vendor.billing?.tax_id || vendor.tax_id || '',
    auto_invoice: !!(vendor.billing?.auto_invoice ?? vendor.auto_invoice),
    invoice_prefix: vendor.billing?.invoice_prefix || vendor.invoice_prefix || '',
    notes: vendor.billing?.notes || vendor.notes || '',
    billing_address: {
      address_line_1: vendor.billing?.billing_address?.address_line_1 || vendor.billing_address?.address_line_1 || '',
      address_line_2: vendor.billing?.billing_address?.address_line_2 || vendor.billing_address?.address_line_2 || '',
      city: vendor.billing?.billing_address?.city || vendor.billing_address?.city || '',
      state: vendor.billing?.billing_address?.state || vendor.billing_address?.state || '',
      pincode: vendor.billing?.billing_address?.pincode || vendor.billing_address?.pincode || '',
      country: vendor.billing?.billing_address?.country || vendor.billing_address?.country || 'India',
      phone: vendor.billing?.billing_address?.phone || vendor.billing_address?.phone || vendor.primary_contact_phone || '',
      landmark: vendor.billing?.billing_address?.landmark || vendor.billing_address?.landmark || '',
    },
  });

  const [savingLoading, setSavingLoading] = useState(false);
  const fetchPaymentProviders = async () => {
    setLoadingPayment(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/${vendor.id}/payment-providers/`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // If 404, check for locally stored integrations
          const localIntegrations = JSON.parse(localStorage.getItem(`payment_integrations_${vendor.id}`) || '[]');
          setPaymentProviders(localIntegrations.length > 0 ? localIntegrations : []);
          return;
        }
        if (response.status === 401) {
          handleRawFetch401();
          setPaymentProviders([]);
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setPaymentProviders(data || []);

      // Also check for locally stored integrations and merge
      const localIntegrations = JSON.parse(localStorage.getItem(`payment_integrations_${vendor.id}`) || '[]');
      if (localIntegrations.length > 0) {
        const apiIds = new Set((data || []).map(p => p.id));
        const uniqueLocal = localIntegrations.filter(p => !apiIds.has(p.id));
        if (uniqueLocal.length > 0) {
          setPaymentProviders([...(data || []), ...uniqueLocal]);
        }
      }
    } catch (err) {
      console.error("Error fetching payment providers:", err);
      // Keeps whatever was last loaded. An empty list here reads as "this
      // vendor has no payment providers", which a failed request cannot show.
    } finally {
      setLoadingPayment(false);
    }
  };

  // Fetch payment providers when Payment Integration tab is active
  useEffect(() => {
    if (activeTab === 'payment-integration' && vendor?.id) {
      fetchPaymentProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, vendor?.id]);

  // Fetch warehouses when Warehouses tab is active
  const fetchWarehouses = async () => {
    if (!vendor?.id) return;
    setLoadingWarehouses(true);
    try {
      const data = await listVendorPickupAddresses(vendor.id);
      const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      setWarehouses(list);
    } catch (err) {
      console.error("Error fetching warehouses:", err);
      // Keeps the last known warehouses rather than asserting there are none.
    } finally {
      setLoadingWarehouses(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'warehouses' && vendor?.id) {
      fetchWarehouses();
    }
  }, [activeTab, vendor?.id]);

  // Fetch contacts when Contact Details tab is active
  const fetchContacts = async () => {
    if (!vendor?.id) return;
    setLoadingContacts(true);
    try {
      const data = await listVendorContacts(vendor.id, { is_active: true });
      const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      setContacts(list);
    } catch (err) {
      console.error("Error fetching contacts:", err);
      // Keeps the last known contacts rather than asserting there are none.
    } finally {
      setLoadingContacts(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'contact-details' && vendor?.id) {
      fetchContacts();
    }
  }, [activeTab, vendor?.id]);

  // Fetch shipping partners when Shipping Partners tab is active
  const fetchShippingPartners = async () => {
    if (!vendor?.id) return;
    setLoadingShippingPartners(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/${vendor.id}/shipping-partners/`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          console.warn("Shipping partners endpoint not found. Checking local storage.");
          const localPartners = JSON.parse(localStorage.getItem(`shipping_partners_${vendor.id}`) || '[]');
          if (localPartners.length > 0) {
            setShippingPartners(localPartners);
          } else {
            // Use mock data for development if API doesn't exist
            setShippingPartners([
              { id: 1, nickname: "Delhivery Express", partner: "delhivery", is_default: false, status: ["Shared", "Connected"] },
              { id: 2, nickname: "ecom express-sdd/ndd- prozo", partner: "prozo", is_default: false, status: ["Shared", "Connected"] },
            ]);
          }
          return;
        }
        if (response.status === 401) {
          handleRawFetch401();
          setShippingPartners([]);
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setShippingPartners(data || []);

      // Also check for locally stored partners and merge
      const localPartners = JSON.parse(localStorage.getItem(`shipping_partners_${vendor.id}`) || '[]');
      if (localPartners.length > 0) {
        // Merge with API data, avoiding duplicates
        const apiIds = new Set((data || []).map(p => p.id));
        const uniqueLocal = localPartners.filter(p => !apiIds.has(p.id));
        if (uniqueLocal.length > 0) {
          setShippingPartners([...(data || []), ...uniqueLocal]);
        }
      }
    } catch (err) {
      console.error("Error fetching shipping partners:", err);
      // For other errors, try to load from local storage as fallback
      const localPartners = JSON.parse(localStorage.getItem(`shipping_partners_${vendor.id}`) || '[]');
      setShippingPartners(localPartners.length > 0 ? localPartners : []);
    } finally {
      setLoadingShippingPartners(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'shipping-partners' && vendor?.id) {
      fetchShippingPartners();
    }
  }, [activeTab, vendor?.id]);


  // Also fetch shipping partners when Shipping Automation sub-tab is active
  useEffect(() => {
    if (activeTab === 'shipping-partners' && shippingSubTab === 'shipping-automation' && vendor?.id) {
      // Only fetch if we don't have partners loaded yet
      if (shippingPartners.length === 0) {
        fetchShippingPartners();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, shippingSubTab, vendor?.id]);

  // Debounced search term state
  const [debouncedProductsSearchTerm, setDebouncedProductsSearchTerm] = useState("");

  // Debounce search term and reset page to 1 when search changes
  useEffect(() => {
    if (activeTab !== 'products') return;

    if (productsSearchDebounceRef.current) {
      clearTimeout(productsSearchDebounceRef.current);
    }

    productsSearchDebounceRef.current = setTimeout(() => {
      const searchChanged = productsSearchTerm !== debouncedProductsSearchTerm;
      setDebouncedProductsSearchTerm(productsSearchTerm);
      // Reset to page 1 only when search term actually changes
      if (searchChanged) {
        setProductsCurrentPage(1);
      }
    }, 500);

    return () => {
      if (productsSearchDebounceRef.current) {
        clearTimeout(productsSearchDebounceRef.current);
      }
    };
  }, [productsSearchTerm, activeTab, debouncedProductsSearchTerm]);

  // Fetch products for vendor
  useEffect(() => {
    if (activeTab !== 'products' || !vendor?.id) return;

    // Create a unique key for this fetch to prevent duplicates
    const fetchKey = `${vendor.id}-${productsCurrentPage}-${debouncedProductsSearchTerm}-${productsFetchTrigger}`;

    // Skip if this exact fetch is already in progress
    if (productsFetchRef.current === fetchKey) {
      return;
    }

    productsFetchRef.current = fetchKey;

    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        setProductsError(null);

        const params = {
          // Server-side pagination now uses limit & offset only
          limit: productsItemsPerPage,
          offset: (productsCurrentPage - 1) * productsItemsPerPage,
          vendor_id: vendor.id,
        };

        if (debouncedProductsSearchTerm && debouncedProductsSearchTerm.trim()) {
          params.search = debouncedProductsSearchTerm.trim();
        }

        const response = await listProducts(params);

        // Only update if this is still the current fetch
        if (productsFetchRef.current === fetchKey) {
          if (response && response.products) {
            setProducts(response.products);
            if (response.pagination) {
              setProductsServerPagination(response.pagination);
            } else {
              setProductsServerPagination(null);
            }
          } else {
            setProducts([]);
            setProductsServerPagination(null);
          }
        }
      } catch (err) {
        console.error('Error fetching vendor products:', err);
        // Only update error if this is still the current fetch
        if (productsFetchRef.current === fetchKey) {
          // The old string embedded the browser's own "Failed to fetch"; the
          // classified description names a cause the user can act on. Products
          // already on screen stay put.
          setProductsError(err && err.classified ? err.classified : classifyError(err));
        }
      } finally {
        // Only update loading if this is still the current fetch
        if (productsFetchRef.current === fetchKey) {
          setProductsLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      // Clear the ref if component unmounts or dependencies change
      if (productsFetchRef.current === fetchKey) {
        productsFetchRef.current = null;
      }
    };
  }, [activeTab, productsCurrentPage, debouncedProductsSearchTerm, vendor?.id, productsItemsPerPage, productsFetchTrigger]);

  // Retry function for error state
  const handleRetryProducts = useCallback(() => {
    setProductsFetchTrigger(prev => prev + 1);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShipperDropdown && !event.target.closest('.shipperCompanyDropdown')) {
        setShowShipperDropdown(false);
      }
    };

    if (showShipperDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showShipperDropdown]);

  // Validation functions
  const validateField = (name, value) => {
    const errors = {};

    switch (name) {
      case 'company_name':
        if (!value || value.trim().length === 0) {
          errors[name] = 'Company name is required';
        } else if (value.trim().length < 2) {
          errors[name] = 'Company name must be at least 2 characters';
        }
        break;
      case 'tally_name':
        // Optional (model is blank=True), but it becomes the CRVW_ Tally ledger
        // name, so an over-long value would be silently truncated at the DB.
        if (value && value.trim().length > 255) {
          errors[name] = 'Tally name must be 255 characters or less';
        }
        break;
      case 'brand_name':
        if (!value || value.trim().length === 0) {
          errors[name] = 'Brand name is required';
        }
        break;
      case 'website':
        if (value && !/^https?:\/\/.+/.test(value)) {
          errors[name] = 'Website must be a valid URL (http:// or https://)';
        }
        break;
      case 'primary_contact_name':
        if (!value || value.trim().length === 0) {
          errors[name] = 'Contact name is required';
        } else if (value.trim().length < 2) {
          errors[name] = 'Contact name must be at least 2 characters';
        }
        break;
      case 'primary_contact_phone':
        if (!value || value.trim().length === 0) {
          errors[name] = 'Phone number is required';
        } else if (!/^[+]?[0-9\s\-\(\)]{10,}$/.test(value)) {
          errors[name] = 'Please enter a valid phone number';
        }
        break;
      case 'status':
        if (!value || !['pending', 'approved', 'inactive'].includes(value)) {
          errors[name] = 'Please select a valid status';
        }
        break;
      case 'public_description':
        if (value && value.length > 500) {
          errors[name] = 'Description must be less than 500 characters';
        }
        break;
      default:
        break;
    }

    return errors;
  };

  const validateSocialMediaLink = (field, value) => {
    if (value && !/^https?:\/\/.+/.test(value)) {
      return { [field]: 'Must be a valid URL (http:// or https://)' };
    }
    return {};
  };

  const tabs = [
    { id: 'company-details', label: 'Company Details', icon: '🏢' },
    { id: 'contact-details', label: 'Contact Details', icon: '📇' },
    { id: 'billing-invoice', label: 'Billing & Invoice', icon: '💰' },
    { id: 'warehouses', label: 'Warehouses', icon: '📦' },
    { id: 'products', label: 'Products', icon: '🛍️' },
    { id: 'shipping-partners', label: 'Shipping Partners', icon: '🚚' },
    { id: 'users-permissions', label: 'Users and Permissions', icon: '👥' },
    { id: 'payment-integration', label: 'Payment Integration', icon: '💳' },
    { id: 'custom-settings', label: 'Custom Vendor Settings', icon: '⚙️' },
    { id: 'sync-settings', label: 'Shopify Sync Settings', icon: '📦' },
    { id: 'connect-shopify', label: 'Connect Shopify', icon: <FaShopify style={{ color: '#95bf47' }} /> },
  ];

  const applyLiveValidation = (field, value) => {
    const nextErrors = {
      ...(field === 'facebook_link' || field === 'instagram_link' || field === 'tiktok_link' || field === 'twitter_link'
        ? validateSocialMediaLink(field, value)
        : validateField(field, value)),
    };

    setValidationErrors((prev) => {
      const updated = { ...prev };
      if (nextErrors[field]) {
        updated[field] = nextErrors[field];
      } else {
        delete updated[field];
      }
      return updated;
    });
  };

  const handleCompanyDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;

    // Nested vendor_settings fields
    const vendorSettingsFields = ['facebook_link', 'instagram_link', 'tiktok_link', 'twitter_link', 'hide_promotion_banner'];

    if (vendorSettingsFields.includes(name)) {
      setCompanyDetails((prev) => ({
        ...prev,
        vendor_settings: {
          ...(prev.vendor_settings || {}),
          [name]: nextValue,
        },
      }));
    } else {
      setCompanyDetails((prev) => ({
        ...prev,
        [name]: nextValue,
      }));
    }

    // Live validations
    if (type !== 'checkbox') {
      applyLiveValidation(name, nextValue);
    }
  };

  const openFilePicker = (ref) => {
    if (ref?.current) ref.current.click();
  };

  const handleLogoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast({ type: 'error', message: 'Logo must be an image file (png/jpg/webp/svg).' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showToast({ type: 'error', message: 'Logo file size must be <= 2MB.' });
      return;
    }

    setLogoFile(file);
    const tempUrl = URL.createObjectURL(file);
    setLogoPreview(tempUrl);
    showToast({ type: 'info', message: 'Logo selected. It will be uploaded when you click SAVE.' });
  };

  const handleDocumentSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    // Basic validation: allow PDFs/images/docs up to 10MB
    if (file.size > 10 * 1024 * 1024) {
      showToast({ type: 'error', message: 'Document file size must be <= 10MB.' });
      return;
    }

    setDocumentFile(file);

    // Show a preview (browser can open many doc types; PDF will work well)
    const tempUrl = URL.createObjectURL(file);
    setDocumentPreview(tempUrl);
    showToast({ type: 'info', message: 'Document selected. It will be uploaded when you click SAVE.' });
  };

  const handlePromotionBannerSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast({ type: 'error', message: 'Promotion banner must be an image file.' });
      return;
    }

    setPromotionBannerFile(file);
    const tempUrl = URL.createObjectURL(file);
    setPromotionBannerPreview(tempUrl);

    showToast({ type: 'info', message: 'Promotion banner selected. It will be uploaded when you click SAVE.' });
  };

  const handleSaveCompanyDetails = async () => {
    // Validate required fields
    const errors = {};
    const trim = (v = '') => String(v).trim();

    if (!trim(companyDetails.company_name)) {
      errors.company_name = 'Company name is required';
    } else if (trim(companyDetails.company_name).length < 2) {
      errors.company_name = 'Company name must be at least 2 characters';
    }

    if (!trim(companyDetails.brand_name)) {
      errors.brand_name = 'Brand name is required';
    }

    if (!trim(companyDetails.primary_contact_name)) {
      errors.primary_contact_name = 'Contact name is required';
    } else if (trim(companyDetails.primary_contact_name).length < 2) {
      errors.primary_contact_name = 'Contact name must be at least 2 characters';
    }

    if (!trim(companyDetails.primary_contact_phone)) {
      errors.primary_contact_phone = 'Phone number is required';
    } else if (!/^[+]?[0-9\s\-\(\)]{10,}$/.test(trim(companyDetails.primary_contact_phone))) {
      errors.primary_contact_phone = 'Please enter a valid phone number';
    }

    if (!companyDetails.status || !['pending', 'approved', 'rejected'].includes(companyDetails.status)) {
      errors.status = 'Please select a valid status';
    }
    // Website is optional but must be a valid URL when provided
    if (trim(companyDetails.website) && !/^https?:\/\/.+/.test(trim(companyDetails.website))) {
      errors.website = 'Website must be a valid URL (http:// or https://)';
    }

    if (companyDetails.public_description && companyDetails.public_description.length > 500) {
      errors.public_description = 'Description must be less than 500 characters';
    }
    // Validate social media links (optional, but must be valid URLs when provided)
    const socialFields = ['facebook_link', 'instagram_link', 'tiktok_link', 'twitter_link'];
    socialFields.forEach((field) => {
      const value = companyDetails.vendor_settings?.[field];
      if (value && value.trim().length > 0 && !/^https?:\/\/.+/.test(value.trim())) {
        errors[field] = 'Must be a valid URL (http:// or https://)';
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showToast({ type: 'error', message: 'Please fix validation errors before saving.' });
      return;
    }

    setValidationErrors({});

    try {
      setIsUploading(true);

      const trim = (v = '') => String(v).trim();

      const vendorSettings = {
        source: companyDetails.vendor_settings?.source || 'onboarding',
        created_by_user_id: companyDetails.vendor_settings?.created_by_user_id || currentUserId || '',
        facebook_link: trim(companyDetails.vendor_settings?.facebook_link),
        instagram_link: trim(companyDetails.vendor_settings?.instagram_link),
        tiktok_link: trim(companyDetails.vendor_settings?.tiktok_link),
        twitter_link: trim(companyDetails.vendor_settings?.twitter_link),
        hide_promotion_banner: !!companyDetails.vendor_settings?.hide_promotion_banner,
      };

      // updateVendor() will automatically use multipart when any file is present.
      const payload = {
        company_name: trim(companyDetails.company_name),
        tally_name: trim(companyDetails.tally_name),
        brand_name: trim(companyDetails.brand_name),
        website: trim(companyDetails.website),
        primary_contact_name: trim(companyDetails.primary_contact_name),
        primary_contact_phone: trim(companyDetails.primary_contact_phone),
        status: (companyDetails.status || 'pending').toLowerCase(),
        public_description: companyDetails.public_description || '',
        vendor_settings: vendorSettings,
        // Compatibility with backend curl example
        facebook_link: vendorSettings.facebook_link,
        instagram_link: vendorSettings.instagram_link,
        tiktok_link: vendorSettings.tiktok_link,
        twitter_link: vendorSettings.twitter_link,
        hide_promotion_banner: vendorSettings.hide_promotion_banner,
        ...(logoFile ? { logo: logoFile } : {}),
        ...(documentFile ? { document: documentFile } : {}),
        ...(promotionBannerFile ? { promotion_banner: promotionBannerFile } : {}),
      };

      const res = await updateVendor(vendor.id, payload);

      // Best-effort: refresh previews from server response if available
      const nextLogoUrl = res?.logo || res?.logo_url;
      const nextDocUrl = res?.documents || res?.document_url;
      if (nextLogoUrl) {
        setCompanyDetails((prev) => ({ ...prev, logo: nextLogoUrl }));
        setLogoPreview(nextLogoUrl);
      }
      if (nextDocUrl) {
        const docUrl = Array.isArray(nextDocUrl) ? (nextDocUrl[0] || "") : (nextDocUrl || "");
        setCompanyDetails((prev) => ({ ...prev, documents: nextDocUrl }));
        setDocumentPreview(docUrl);
      }

      setLogoFile(null);
      setDocumentFile(null);
      setPromotionBannerFile(null);
      showToast({ type: 'success', message: 'Company details saved successfully!' });
    } catch (err) {
      console.error('Error saving company details:', err);
      showToast({ type: 'error', message: `Failed to save company details: ${err.message || 'Please try again.'}` });
    } finally {
      setIsUploading(false);
    }
  };

  const handleConnectShopify = async (e) => {
    e.preventDefault();
    if (!shopifyConnectData.shopify_shop_name || !shopifyConnectData.shopify_client_id || !shopifyConnectData.shopify_client_secret) {
      showToast({ type: 'error', message: 'Please fill in all Shopify credentials.' });
      return;
    }

    setConnectingShopify(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/shipturtle/vendors/connect-shopify/`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...shopifyConnectData }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}`);
      }

      // Webhook registration now happens automatically when connecting Shopify store
      showToast({ type: 'success', message: 'Shopify connected successfully!' });

      // Refresh config to update UI status
      fetchShopifyConfig();

    } catch (error) {
      console.error("Error connecting Shopify:", error);
      showToast({ type: 'error', message: `Failed to connect Shopify: ${error.message || 'Check credentials.'}` });
    } finally {
      setConnectingShopify(false);
    }
  };

  const getTopicInfo = (topic) => {
    switch (topic) {
      case 'PRODUCTS_UPDATE':
      case 'products/update':
        return { label: 'Product Update', color: '#059669', bg: '#ecfdf5' };
      case 'ORDERS_UPDATED':
      case 'orders/updated':
      case 'orders/create':
      case 'ORDERS_CREATE':
        return { label: 'Order Update', color: '#2563eb', bg: '#eff6ff' };
      default: {
        const cleanLabel = topic.includes('/') ? topic.split('/')[1] : topic;
        return {
          label: cleanLabel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          color: '#4b5563',
          bg: '#f3f4f6'
        };
      }
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({ type: 'success', message: 'Copied to clipboard!' });
    } catch (err) {
      console.error('Failed to copy:', err);
      showToast({ type: 'error', message: 'Failed to copy to clipboard' });
    }
  };

  const renderWebhooksSection = () => {
    return (
      <div className="webhooksSection" style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Active Shopify Webhooks</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
              {webhooks.length} {webhooks.length === 1 ? 'webhook' : 'webhooks'} active
            </span>
            <button
              type="button"
              onClick={fetchWebhooks}
              disabled={webhooksLoading}
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                cursor: webhooksLoading ? 'not-allowed' : 'pointer',
                color: '#64748b',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              title="Refresh webhooks list"
            >
              <FaSync className={webhooksLoading ? 'spin' : ''} style={{ fontSize: '14px' }} />
            </button>
          </div>
        </div>

        {webhooksLoading && webhooks.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '40px 0', gap: '12px' }}>
            <div className="spinner" style={{ width: '24px', height: '24px', borderLeftColor: '#95bf47' }}></div>
          </div>
        ) : webhooksError ? (
          <div style={{ padding: '20px', textAlign: 'center', background: '#fef2f2', borderRadius: '8px' }}>
            <p style={{ color: '#991b1b', fontSize: '13px', margin: '0 0 10px 0' }}>{webhooksError}</p>
            <button type="button" onClick={fetchWebhooks} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer' }}>Retry</button>
          </div>
        ) : (!hasShopifyConnected || webhooks.length === 0) ? (
          <div style={{ padding: '30px 20px', textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: '8px' }}>
            <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>No active webhooks registered.</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            {webhooks.map((webhook, idx) => {
              const topicInfo = getTopicInfo(webhook.topic);
              const callbackUrl = webhook.callback_url || webhook.address;

              return (
                <div key={idx} style={{
                  padding: '16px',
                  background: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  {/* Header: Topic Badge + Status */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{
                      fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '6px',
                      background: topicInfo.bg, color: topicInfo.color, textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>{topicInfo.label}</span>
                    <span style={{
                      fontSize: '10px', fontWeight: '600', padding: '3px 8px', borderRadius: '6px',
                      background: '#dcfce7', color: '#15803d', display: 'flex', alignItems: 'center', gap: '4px'
                    }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e' }}></span>
                      Active
                    </span>
                  </div>

                  {/* Shop Domain */}
                  {webhook.shop_domain && (
                    <div style={{
                      fontSize: '12px',
                      color: '#475569',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <FaShopify style={{ color: '#94a3b8', fontSize: '12px' }} />
                      {webhook.shop_domain}
                    </div>
                  )}

                  {/* Callback URL with Copy */}
                  {callbackUrl && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      background: '#f8fafc',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{
                        flex: 1,
                        fontSize: '11px',
                        color: '#64748b',
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        lineHeight: '1.5'
                      }}>
                        {callbackUrl}
                      </div>
                      <button
                        onClick={() => copyToClipboard(callbackUrl)}
                        style={{
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#64748b',
                          transition: 'all 0.2s',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f97316';
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.borderColor = '#f97316';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.color = '#64748b';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                        title="Copy callback URL"
                      >
                        <FaCopy style={{ fontSize: '12px' }} />
                      </button>
                    </div>
                  )}

                  {/* Badges Row: API Version + Format */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    {webhook.api_version && (
                      <span style={{
                        fontSize: '10px',
                        fontWeight: '600',
                        padding: '3px 8px',
                        borderRadius: '6px',
                        background: '#f1f5f9',
                        color: '#64748b'
                      }}>
                        API: {webhook.api_version}
                      </span>
                    )}
                    {webhook.format && (
                      <span style={{
                        fontSize: '10px',
                        fontWeight: '600',
                        padding: '3px 8px',
                        borderRadius: '6px',
                        background: '#f1f5f9',
                        color: '#64748b'
                      }}>
                        {webhook.format}
                      </span>
                    )}
                  </div>

                  {/* Remove button for PRODUCT UPDATE and ORDER UPDATE webhooks */}
                  {(webhook.topic === 'PRODUCTS_UPDATE' || webhook.topic === 'products/update' ||
                    webhook.topic === 'ORDERS_UPDATED' || webhook.topic === 'orders/updated' ||
                    webhook.topic === 'orders/create' || webhook.topic === 'ORDERS_CREATE') && (
                      <button
                        type="button"
                        onClick={() => {
                          const type = (webhook.topic === 'PRODUCTS_UPDATE' || webhook.topic === 'products/update') ? 'product' : 'order';
                          setWebhookTypeToRemove(type);
                          setShowRemoveWebhookModal(true);
                        }}
                        style={{
                          marginTop: '4px',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #fecaca',
                          background: '#fef2f2',
                          color: '#dc2626',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          alignSelf: 'flex-start'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#dc2626';
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.borderColor = '#dc2626';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fef2f2';
                          e.currentTarget.style.color = '#dc2626';
                          e.currentTarget.style.borderColor = '#fecaca';
                        }}
                      >
                        Remove
                      </button>
                    )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderConnectShopify = () => {
    return (
      <div className="tabContent">
        <div className="shopifyConnectContainer" style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
          {/* Header with connection badge */}
          <div className="shopifyConnectHeader" style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
            {hasShopifyConnected ? (
              <span className="syncBadge synced" style={{ fontSize: '13px', background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0', padding: '6px 14px', borderRadius: '20px', fontWeight: 'bold' }}>
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', marginRight: '8px' }}></span>
                CONNECTED
              </span>
            ) : (
              <span className="syncBadge notSynced" style={{ fontSize: '13px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', padding: '6px 14px', borderRadius: '20px', fontWeight: 'bold' }}>
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#94a3b8', marginRight: '8px' }}></span>
                NOT CONNECTED
              </span>
            )}
            {hasShopifyConnected && shopifyWebhookId && (
              <span className="syncBadge synced" style={{ fontSize: '13px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '6px 14px', borderRadius: '20px' }}>
                <FaShopify style={{ marginRight: '6px' }} /> Auto-sync enabled
              </span>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Row 1: Instructions and Update Connection Form side-by-side */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))',
              gap: '24px',
              alignItems: 'stretch'
            }}>
              {/* Instructions Column (Left) */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>💡</span> How to Connect Your Shopify Store
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', flex: 1 }}>
                  {[
                    { s: 1, t: 'Shopify Subdomain', d: 'Enter store name without .myshopify.com (e.g. "mystore")' },
                    { s: 2, t: 'API Key (Client ID)', d: 'Located in Shopify Admin → Settings → Develop Apps → API Credentials' },
                    { s: 3, t: 'Access Token (Secret)', d: 'Admin API access token from the same credentials page' },
                    { s: 4, t: 'Click Connect', d: 'Hit the button to establish a persistent sync connection' }
                  ].map((item) => (
                    <div key={item.s} style={{ display: 'flex', gap: '12px' }}>
                      <div style={{
                        background: '#00bfae', color: 'white', width: '22px', height: '22px', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '11px', flexShrink: 0
                      }}>{item.s}</div>
                      <div>
                        <p style={{ margin: '0 0 2px 0', fontWeight: '700', color: '#1e293b', fontSize: '13px' }}>{item.t}</p>
                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>{item.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Update Connection Form Column (Middle/Between) */}
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {vendor?.shopify_sync_status === 'failed' && (
                  <div style={{ background: '#fff1f2', border: '1px solid #ffe4e6', borderRadius: '8px', padding: '12px 16px', color: '#e11d48', marginBottom: '16px', fontSize: '13px', fontWeight: '500' }}>
                    ⚠️ Connection error. Please verify and re-save credentials.
                  </div>
                )}
                <form onSubmit={handleConnectShopify} className="edit-card" style={{ padding: '24px', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="formGroup">
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569', marginBottom: '6px' }}>Shop Subdomain</label>
                      <div style={{ position: 'relative' }}>
                        <input type="text" className="formInput" placeholder="store-name" value={shopifyConnectData.shopify_shop_name} onChange={(e) => setShopifyConnectData({ ...shopifyConnectData, shopify_shop_name: e.target.value })} style={{ paddingRight: '110px', fontSize: '13px', height: '38px' }} />
                        <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '11px' }}>.myshopify.com</span>
                      </div>
                    </div>
                    <div className="formGroup">
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569', marginBottom: '6px' }}>Store Tag Name</label>
                      <input type="text" className="formInput" placeholder="Vendor Tag" value={shopifyConnectData.vendor_name} onChange={(e) => setShopifyConnectData({ ...shopifyConnectData, vendor_name: e.target.value })} style={{ fontSize: '13px', height: '38px' }} />
                    </div>
                    <div className="formGroup">
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569', marginBottom: '6px' }}>Client ID</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showShopifyClientId ? "text" : "password"} className="formInput" placeholder="..." value={shopifyConnectData.shopify_client_id} onChange={(e) => setShopifyConnectData({ ...shopifyConnectData, shopify_client_id: e.target.value })} style={{ paddingRight: '40px', fontSize: '13px', height: '38px' }} />
                        <button type="button" onClick={() => setShowShopifyClientId(!showShopifyClientId)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                          {showShopifyClientId ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>
                    <div className="formGroup">
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569', marginBottom: '6px' }}>Client Secret</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showShopifyClientSecret ? "text" : "password"} className="formInput" placeholder="..." value={shopifyConnectData.shopify_client_secret} onChange={(e) => setShopifyConnectData({ ...shopifyConnectData, shopify_client_secret: e.target.value })} style={{ paddingRight: '40px', fontSize: '13px', height: '38px' }} />
                        <button type="button" onClick={() => setShowShopifyClientSecret(!showShopifyClientSecret)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                          {showShopifyClientSecret ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="submit" className="createBtn" style={{ background: '#95bf47', padding: '10px 24px', fontSize: '14px', borderRadius: '8px', fontWeight: 'bold' }} disabled={connectingShopify || loadingShopifyConfig}>
                      {connectingShopify ? 'Connecting...' : (hasShopifyConnected ? 'Update Connection' : 'Connect Store')}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Row 2: Active Webhooks List (Full Width / Down that) */}
            {hasShopifyConnected && (
              <div style={{ width: '100%' }}>
                {renderWebhooksSection()}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCompanyDetails = () => {
    return (
      <div className="tabContent">
        <p className="instructionText">
          These details will be used as billing details on customer invoices in case of marketplace vendors
        </p>

        {/* Basic Info Section */}
        <div className="formSection">
          <h4 className="sectionTitle">Basic Info</h4>

          {/* Company Logo */}
          <div className="companyLogoSection">
            <div className="companyLogoPlaceholder">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Vendor logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <>
                  <FaCameraRetro className="cameraIconLarge" />
                  <span className="photoComingSoon">Upload logo</span>
                </>
              )}

              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleLogoSelected}
              />

              <button
                type="button"
                className="browseBtn"
                onClick={() => openFilePicker(logoInputRef)}
                disabled={isUploading}
                title="Upload logo"
                style={{ position: 'absolute', bottom: 8, left: 8 }}
              >
                Browse
              </button>

              {logoPreview && (
                <button
                  type="button"
                  className="deleteLogoBtn"
                  title="Remove logo"
                  onClick={() => {
                    setCompanyDetails((prev) => ({ ...prev, logo: '' }));
                    setLogoFile(null);
                    setLogoPreview('');
                  }}
                >
                  <FaTrash />
                </button>
              )}
            </div>
          </div>

          {/* Basic Info Fields - Two Column Grid */}
          <div className="formGridTwo">
            <div className="formGroup">
              <label>Company Name</label>
              <input
                type="text"
                name="company_name"
                className={`formInput ${validationErrors.company_name ? 'error' : ''}`}
                placeholder="Enter company Name"
                value={companyDetails.company_name}
                onChange={handleCompanyDetailsChange}
              />
              {validationErrors.company_name && <span className="errorText">{validationErrors.company_name}</span>}
            </div>

            <div className="formGroup">
              <label>Tally Name</label>
              <input
                type="text"
                name="tally_name"
                className={`formInput ${validationErrors.tally_name ? 'error' : ''}`}
                placeholder="Enter Tally Name"
                value={companyDetails.tally_name}
                onChange={handleCompanyDetailsChange}
              />
              {validationErrors.tally_name && <span className="errorText">{validationErrors.tally_name}</span>}
            </div>

            <div className="formGroup">
              <label>Brand Name</label>
              <input
                type="text"
                name="brand_name"
                className={`formInput ${validationErrors.brand_name ? 'error' : ''}`}
                placeholder="Enter Brand Name"
                value={companyDetails.brand_name}
                onChange={handleCompanyDetailsChange}
              />
              {validationErrors.brand_name && <span className="errorText">{validationErrors.brand_name}</span>}
            </div>

            <div className="formGroup">
              <label>Website</label>
              <input
                type="text"
                name="website"
                className={`formInput ${validationErrors.website ? 'error' : ''}`}
                placeholder="Enter website"
                value={companyDetails.website}
                onChange={handleCompanyDetailsChange}
              />
              {validationErrors.website && <span className="errorText">{validationErrors.website}</span>}
            </div>

            <div className="formGroup">
              <label>Contact Name</label>
              <input
                type="text"
                name="primary_contact_name"
                className={`formInput ${validationErrors.primary_contact_name ? 'error' : ''}`}
                placeholder="Enter Contact Name"
                value={companyDetails.primary_contact_name}
                onChange={handleCompanyDetailsChange}
              />
              {validationErrors.primary_contact_name && <span className="errorText">{validationErrors.primary_contact_name}</span>}
            </div>

            <div className="formGroup">
              <label>Phone</label>
              <input
                type="text"
                name="primary_contact_phone"
                className={`formInput ${validationErrors.primary_contact_phone ? 'error' : ''}`}
                placeholder="Enter Phone"
                value={companyDetails.primary_contact_phone}
                onChange={handleCompanyDetailsChange}
              />
              {validationErrors.primary_contact_phone && <span className="errorText">{validationErrors.primary_contact_phone}</span>}
            </div>

            <div className="formGroup">
              <label>Status</label>
              <select
                name="status"
                className={`formInput ${validationErrors.status ? 'error' : ''}`}
                value={(companyDetails.status || 'pending').toLowerCase()}
                onChange={handleCompanyDetailsChange}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              {validationErrors.status && <span className="errorText">{validationErrors.status}</span>}
            </div>
          </div>
        </div>

        {/* Public Description */}
        <div className="formSection">
          <h4 className="sectionTitle">Public Description</h4>
          <div className="formGroup">
            <textarea
              name="public_description"
              className={`formInput ${validationErrors.public_description ? 'error' : ''}`}
              placeholder="Public description..."
              rows={4}
              value={companyDetails.public_description}
              onChange={handleCompanyDetailsChange}
            />
            {validationErrors.public_description && <span className="errorText">{validationErrors.public_description}</span>}
          </div>
        </div>

        {/* Upload Document Section */}
        <div className="formSection">
          <h4 className="sectionTitle">Upload Document</h4>
          <div className="uploadArea">
            <FaCloudUploadAlt className="uploadIcon" />
            <p>{documentPreview ? 'Document uploaded' : 'Choose a document to upload'}</p>

            <input
              type="file"
              ref={documentInputRef}
              style={{ display: 'none' }}
              onChange={handleDocumentSelected}
            />

            <button
              type="button"
              className="browseBtn"
              onClick={() => openFilePicker(documentInputRef)}
              disabled={isUploading}
            >
              Browse
            </button>

            {documentPreview && (
              <div style={{ marginTop: '0.75rem' }}>
                <a href={documentPreview} target="_blank" rel="noreferrer">
                  View uploaded document
                </a>
                <button
                  type="button"
                  className="deleteLogoBtn"
                  title="Remove document"
                  onClick={() => {
                    setCompanyDetails((prev) => ({ ...prev, documents: '' }));
                    setDocumentFile(null);
                    setDocumentPreview('');
                  }}
                  style={{ marginLeft: '0.75rem' }}
                >
                  <FaTrash />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Promotion Banner (selection only; backend not in PATCH payload) */}
        <div className="formSection">
          <h4 className="sectionTitle">Promotion Banner</h4>
          <div className="uploadArea">
            <FaCloudUploadAlt className="uploadIcon" />
            <p>{promotionBannerPreview ? 'Banner selected' : 'Choose a banner image'}</p>

            <input
              type="file"
              ref={promotionBannerInputRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePromotionBannerSelected}
            />
            <button type="button" className="browseBtn" onClick={() => openFilePicker(promotionBannerInputRef)}>
              Browse
            </button>

            {promotionBannerPreview && (
              <div style={{ marginTop: '0.75rem' }}>
                <img src={promotionBannerPreview} alt="Promotion banner preview" style={{ maxWidth: 320, borderRadius: 8 }} />
              </div>
            )}
          </div>
        </div>

        {/* Social Media Info Section */}
        <div className="formSection">
          <h4 className="sectionTitle">Social Media Info</h4>
          <div className="formGridTwo">
            <div className="formGroup">
              <label>Facebook Link</label>
              <input
                type="text"
                name="facebook_link"
                className={`formInput ${validationErrors.facebook_link ? 'error' : ''}`}
                placeholder="Facebook Link"
                value={companyDetails.vendor_settings?.facebook_link || ''}
                onChange={handleCompanyDetailsChange}
              />
              {validationErrors.facebook_link && <span className="errorText">{validationErrors.facebook_link}</span>}
            </div>

            <div className="formGroup">
              <label>Instagram Link</label>
              <input
                type="text"
                name="instagram_link"
                className={`formInput ${validationErrors.instagram_link ? 'error' : ''}`}
                placeholder="Instagram Link"
                value={companyDetails.vendor_settings?.instagram_link || ''}
                onChange={handleCompanyDetailsChange}
              />
              {validationErrors.instagram_link && <span className="errorText">{validationErrors.instagram_link}</span>}
            </div>

            <div className="formGroup">
              <label>Tiktok Link</label>
              <input
                type="text"
                name="tiktok_link"
                className={`formInput ${validationErrors.tiktok_link ? 'error' : ''}`}
                placeholder="Tiktok Link"
                value={companyDetails.vendor_settings?.tiktok_link || ''}
                onChange={handleCompanyDetailsChange}
              />
              {validationErrors.tiktok_link && <span className="errorText">{validationErrors.tiktok_link}</span>}
            </div>

            <div className="formGroup">
              <label>Twitter Link</label>
              <input
                type="text"
                name="twitter_link"
                className={`formInput ${validationErrors.twitter_link ? 'error' : ''}`}
                placeholder="Twitter Link"
                value={companyDetails.vendor_settings?.twitter_link || ''}
                onChange={handleCompanyDetailsChange}
              />
              {validationErrors.twitter_link && <span className="errorText">{validationErrors.twitter_link}</span>}
            </div>
          </div>

          <div className="formGroup">
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="hide_promotion_banner"
                checked={!!companyDetails.vendor_settings?.hide_promotion_banner}
                onChange={handleCompanyDetailsChange}
              />
              Hide Promotion Banner
            </label>
          </div>
        </div>

        {/* Save Button */}
        <div className="formActions">
          <button type="button" className="saveButton" onClick={handleSaveCompanyDetails} disabled={isUploading}>
            SAVE
          </button>
        </div>

        {/* Show Logs Link */}
        <div className="showLogsLink">
          <a href="#" onClick={(e) => { e.preventDefault(); showToast({ type: 'info', message: 'Show logs functionality coming soon' }); }}>
            + Show Logs
          </a>
        </div>
      </div>
    );
  };

  const setBillingError = (field, message) => {
    setBillingErrors((prev) => {
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  };

  const validateBilling = (data) => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[+]?[0-9\s\-\(\)]{10,}$/;

    const trim = (v = '') => String(v).trim();
    const address = data.billing_address || {};

    if (!trim(data.billing_cycle)) {
      errors.billing_cycle = 'Billing cycle is required.';
    }

    if (!trim(data.payment_terms)) {
      errors.payment_terms = 'Payment terms are required.';
    }

    if (!trim(data.billing_email)) {
      errors.billing_email = 'Billing email is required.';
    } else if (!emailRegex.test(trim(data.billing_email))) {
      errors.billing_email = 'Please enter a valid billing email.';
    }

    const cc = Array.isArray(data.cc_emails) ? data.cc_emails : [];
    if (!cc.length) {
      errors.cc_emails = 'At least one CC email is required.';
    }
    const invalidCc = cc.filter((e) => e && !emailRegex.test(e));
    if (invalidCc.length > 0) {
      errors.cc_emails = `Invalid CC emails: ${invalidCc.join(', ')}`;
    }

    if (!trim(data.currency)) {
      errors.currency = 'Currency is required.';
    }

    if (!trim(data.tax_id)) {
      errors.tax_id = 'Tax ID is required.';
    }

    if (!trim(data.invoice_prefix)) {
      errors.invoice_prefix = 'Invoice prefix is required.';
    }

    if (!trim(data.notes)) {
      errors.notes = 'Notes are required.';
    }

    if (!trim(address.address_line_1)) {
      errors.billing_address_line_1 = 'Address Line 1 is required.';
    }

    if (!trim(address.address_line_2)) {
      errors.billing_address_line_2 = 'Address Line 2 is required.';
    }

    if (!trim(address.city)) {
      errors.billing_city = 'City is required.';
    }

    if (!trim(address.state)) {
      errors.billing_state = 'State is required.';
    }

    const trimmedPincode = trim(address.pincode);
    if (!trimmedPincode) {
      errors.billing_pincode = 'Pincode is required.';
    } else if (!/^[0-9]{4,10}$/.test(trimmedPincode)) {
      errors.billing_pincode = 'Pincode must be 4-10 digits.';
    }

    if (!trim(address.country)) {
      errors.billing_country = 'Country is required.';
    }

    const trimmedPhone = trim(address.phone);
    if (!trimmedPhone) {
      errors.billing_phone = 'Phone is required.';
    } else if (!phoneRegex.test(trimmedPhone)) {
      errors.billing_phone = 'Please enter a valid phone number.';
    }

    if (!trim(address.landmark)) {
      errors.billing_landmark = 'Landmark is required.';
    }

    return errors;
  };

  const handleBillingChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setBillingData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    // live validations
    if (name === 'billing_email') {
      setBillingError('billing_email', nextValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextValue) ? 'Please enter a valid billing email.' : '');
    }
  };

  const handleBillingAddressChange = (e) => {
    const { name, value } = e.target;
    setBillingData((prev) => ({
      ...prev,
      billing_address: {
        ...(prev.billing_address || {}),
        [name]: value,
      },
    }));

    if (name === 'phone') {
      setBillingError('billing_phone', value && !/^[+]?[0-9\s\-\(\)]{10,}$/.test(value) ? 'Please enter a valid phone number.' : '');
    }
    if (name === 'pincode') {
      setBillingError('billing_pincode', value && !/^[0-9]{4,10}$/.test(String(value).trim()) ? 'Pincode must be 4-10 digits.' : '');
    }
  };

  const handleBillingCCEmailsChange = (e) => {
    const raw = e.target.value;
    const emails = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    setBillingData((prev) => ({ ...prev, cc_emails: emails }));

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalid = emails.filter((x) => !emailRegex.test(x));
    setBillingError('cc_emails', invalid.length ? `Invalid CC emails: ${invalid.join(', ')}` : '');
  };

  const handleSaveBillingInvoice = async () => {
    const trim = (v = '') => String(v).trim();
    const payload = {
      billing_cycle: trim(billingData.billing_cycle),
      payment_terms: trim(billingData.payment_terms),
      billing_email: trim(billingData.billing_email),
      cc_emails: (billingData.cc_emails || []).map((e) => trim(e)).filter(Boolean),
      currency: trim(billingData.currency),
      tax_id: trim(billingData.tax_id),
      auto_invoice: !!billingData.auto_invoice,
      invoice_prefix: trim(billingData.invoice_prefix),
      notes: trim(billingData.notes),
      billing_address: {
        address_line_1: trim(billingData.billing_address?.address_line_1),
        address_line_2: trim(billingData.billing_address?.address_line_2),
        city: trim(billingData.billing_address?.city),
        state: trim(billingData.billing_address?.state),
        pincode: trim(billingData.billing_address?.pincode),
        country: trim(billingData.billing_address?.country),
        phone: trim(billingData.billing_address?.phone),
        landmark: trim(billingData.billing_address?.landmark),
      },
    };

    const errors = validateBilling(payload);
    setBillingErrors(errors);
    if (Object.keys(errors).length > 0) {
      showToast({ type: 'error', message: 'Please fix billing validation errors before saving.' });
      return;
    }

    try {
      await updateVendorBilling(vendor.id, payload);
      showToast({ type: 'success', message: 'Billing & Invoice settings saved successfully!' });
    } catch (err) {
      console.error('Failed to save billing settings:', err);
      showToast({ type: 'error', message: `Failed to save billing settings: ${err.message || 'Unknown error'}` });
    }
  };

  const renderBillingInvoice = () => {
    return (
      <div className="tabContent">
        <div className="formSection">
          <h3>Billing & Invoice</h3>

          <div className="formGridTwo">
            <div className="formGroup">
              <label>Billing Cycle <span className="required">*</span></label>
              <select
                name="billing_cycle"
                value={billingData.billing_cycle}
                onChange={handleBillingChange}
                className={`formInput ${billingErrors.billing_cycle ? 'error' : ''}`}
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="weekly">Weekly</option>
              </select>
              {billingErrors.billing_cycle && <span className="errorText">{billingErrors.billing_cycle}</span>}
            </div>

            <div className="formGroup">
              <label>Payment Terms <span className="required">*</span></label>
              <select
                name="payment_terms"
                value={billingData.payment_terms}
                onChange={handleBillingChange}
                className={`formInput ${billingErrors.payment_terms ? 'error' : ''}`}
              >
                <option value="net_30">Net 30</option>
                <option value="net_15">Net 15</option>
                <option value="due_on_receipt">Due on receipt</option>
              </select>
              {billingErrors.payment_terms && <span className="errorText">{billingErrors.payment_terms}</span>}
            </div>

            <div className="formGroup">
              <label>Billing Email <span className="required">*</span></label>
              <input
                type="email"
                name="billing_email"
                value={billingData.billing_email}
                onChange={handleBillingChange}
                className={`formInput ${billingErrors.billing_email ? 'error' : ''}`}
                placeholder="billing@company.com"
              />
              {billingErrors.billing_email && <span className="errorText">{billingErrors.billing_email}</span>}
            </div>

            <div className="formGroup">
              <label>CC Emails (comma separated)</label>
              <input
                type="text"
                value={(billingData.cc_emails || []).join(', ')}
                onChange={handleBillingCCEmailsChange}
                className={`formInput ${billingErrors.cc_emails ? 'error' : ''}`}
                placeholder="finance@company.com, accounts@company.com"
              />
              {billingErrors.cc_emails && <span className="errorText">{billingErrors.cc_emails}</span>}
            </div>

            <div className="formGroup">
              <label>Currency <span className="required">*</span></label>
              <select
                name="currency"
                value={billingData.currency}
                onChange={handleBillingChange}
                className={`formInput ${billingErrors.currency ? 'error' : ''}`}
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
              {billingErrors.currency && <span className="errorText">{billingErrors.currency}</span>}
            </div>

            <div className="formGroup">
              <label>Tax ID <span className="required">*</span></label>
              <input
                type="text"
                name="tax_id"
                value={billingData.tax_id}
                onChange={handleBillingChange}
                className={`formInput ${billingErrors.tax_id ? 'error' : ''}`}
                placeholder="29ABCDE1234F1Z5"
              />
              {billingErrors.tax_id && <span className="errorText">{billingErrors.tax_id}</span>}
            </div>

            <div className="formGroup">
              <label className="checkboxLabel">
                <input type="checkbox" name="auto_invoice" checked={!!billingData.auto_invoice} onChange={handleBillingChange} />
                Auto Invoice
              </label>
            </div>

            <div className="formGroup">
              <label>Invoice Prefix <span className="required">*</span></label>
              <input
                type="text"
                name="invoice_prefix"
                value={billingData.invoice_prefix}
                onChange={handleBillingChange}
                className={`formInput ${billingErrors.invoice_prefix ? 'error' : ''}`}
                placeholder="INV"
              />
              {billingErrors.invoice_prefix && <span className="errorText">{billingErrors.invoice_prefix}</span>}
            </div>
          </div>

          <div className="formGroup" style={{ marginTop: '1rem' }}>
            <label>Notes <span className="required">*</span></label>
            <textarea
              name="notes"
              value={billingData.notes}
              onChange={handleBillingChange}
              className={`formInput ${billingErrors.notes ? 'error' : ''}`}
              rows={3}
              placeholder="Monthly billing with Net 30 terms"
            />
            {billingErrors.notes && <span className="errorText">{billingErrors.notes}</span>}
          </div>

          <div className="formSection" style={{ marginTop: '1.5rem' }}>
            <h4 className="sectionTitle">Billing Address</h4>
            <div className="formGridTwo">
              <div className="formGroup">
                <label>Address Line 1 <span className="required">*</span></label>
                <input
                  name="address_line_1"
                  value={billingData.billing_address.address_line_1}
                  onChange={handleBillingAddressChange}
                  className={`formInput ${billingErrors.billing_address_line_1 ? 'error' : ''}`}
                />
                {billingErrors.billing_address_line_1 && <span className="errorText">{billingErrors.billing_address_line_1}</span>}
              </div>
              <div className="formGroup">
                <label>Address Line 2 <span className="required">*</span></label>
                <input
                  name="address_line_2"
                  value={billingData.billing_address.address_line_2}
                  onChange={handleBillingAddressChange}
                  className={`formInput ${billingErrors.billing_address_line_2 ? 'error' : ''}`}
                />
                {billingErrors.billing_address_line_2 && <span className="errorText">{billingErrors.billing_address_line_2}</span>}
              </div>
              <div className="formGroup">
                <label>City <span className="required">*</span></label>
                <input
                  name="city"
                  value={billingData.billing_address.city}
                  onChange={handleBillingAddressChange}
                  className={`formInput ${billingErrors.billing_city ? 'error' : ''}`}
                />
                {billingErrors.billing_city && <span className="errorText">{billingErrors.billing_city}</span>}
              </div>
              <div className="formGroup">
                <label>State <span className="required">*</span></label>
                <input
                  name="state"
                  value={billingData.billing_address.state}
                  onChange={handleBillingAddressChange}
                  className={`formInput ${billingErrors.billing_state ? 'error' : ''}`}
                />
                {billingErrors.billing_state && <span className="errorText">{billingErrors.billing_state}</span>}
              </div>
              <div className="formGroup">
                <label>Pincode <span className="required">*</span></label>
                <input
                  name="pincode"
                  value={billingData.billing_address.pincode}
                  onChange={handleBillingAddressChange}
                  className={`formInput ${billingErrors.billing_pincode ? 'error' : ''}`}
                />
                {billingErrors.billing_pincode && <span className="errorText">{billingErrors.billing_pincode}</span>}
              </div>
              <div className="formGroup">
                <label>Country <span className="required">*</span></label>
                <input
                  name="country"
                  value={billingData.billing_address.country}
                  onChange={handleBillingAddressChange}
                  className={`formInput ${billingErrors.billing_country ? 'error' : ''}`}
                />
                {billingErrors.billing_country && <span className="errorText">{billingErrors.billing_country}</span>}
              </div>
              <div className="formGroup">
                <label>Phone <span className="required">*</span></label>
                <input
                  name="phone"
                  value={billingData.billing_address.phone}
                  onChange={handleBillingAddressChange}
                  className={`formInput ${billingErrors.billing_phone ? 'error' : ''}`}
                />
                {billingErrors.billing_phone && <span className="errorText">{billingErrors.billing_phone}</span>}
              </div>
              <div className="formGroup">
                <label>Landmark <span className="required">*</span></label>
                <input
                  name="landmark"
                  value={billingData.billing_address.landmark}
                  onChange={handleBillingAddressChange}
                  className={`formInput ${billingErrors.billing_landmark ? 'error' : ''}`}
                />
                {billingErrors.billing_landmark && <span className="errorText">{billingErrors.billing_landmark}</span>}
              </div>
            </div>
          </div>

          <div className="formActions">
            <button type="button" className="saveButton" onClick={handleSaveBillingInvoice}>
              SAVE
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleWarehouseInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Address nested fields
    if (['line1', 'line2', 'city', 'state', 'pincode', 'country', 'phone', 'landmark'].includes(name)) {
      setNewWarehouse((prev) => ({
        ...prev,
        address: {
          ...(prev.address || {}),
          [name]: value,
        },
      }));
      setWarehouseErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    setNewWarehouse((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setWarehouseErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateWarehouseForm = (data) => {
    const errors = {};
    const trim = (v = '') => String(v).trim();
    const phoneRegex = /^[+]?[0-9\s\-\(\)]{10,}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const address = data.address || {};

    if (!trim(data.warehouse_name)) errors.warehouse_name = 'Warehouse name is required';
    if (!trim(data.contact_person)) errors.contact_person = 'Contact person is required';

    const contactNumber = trim(data.contact_number);
    if (!contactNumber) errors.contact_number = 'Contact number is required';
    else if (!phoneRegex.test(contactNumber)) errors.contact_number = 'Contact number must be valid (10+ digits)';

    const contactEmail = trim(data.contact_email);
    if (!contactEmail) errors.contact_email = 'Contact email is required';
    else if (!emailRegex.test(contactEmail)) errors.contact_email = 'Contact email must be valid';

    if (!trim(data.gstin)) errors.gstin = 'GSTIN is required';

    if (!trim(address.line1)) errors.line1 = 'Address line 1 is required';
    if (!trim(address.line2)) errors.line2 = 'Address line 2 is required';
    if (!trim(address.city)) errors.city = 'City is required';
    if (!trim(address.state)) errors.state = 'State is required';

    const pincode = trim(address.pincode);
    if (!pincode) errors.pincode = 'Pincode is required';
    else if (!/^[0-9]{6}$/.test(pincode)) errors.pincode = 'Pincode must be exactly 6 digits';

    if (!trim(address.country)) errors.country = 'Country is required';

    const addressPhone = trim(address.phone || contactNumber);
    if (!addressPhone) errors.phone = 'Address phone is required';
    else if (!phoneRegex.test(addressPhone)) errors.phone = 'Address phone must be valid (10+ digits)';

    if (!trim(address.landmark)) errors.landmark = 'Landmark is required';

    return errors;
  };

  const handleAddWarehouse = async (e) => {
    e.preventDefault();
    try {
      const errors = validateWarehouseForm(newWarehouse);
      setWarehouseErrors(errors);
      if (Object.keys(errors).length > 0) {
        const firstError = Object.values(errors)[0];
        showToast({ type: 'error', message: firstError });
        return;
      }

      const payload = {
        warehouse_name: newWarehouse.warehouse_name.trim(),
        contact_person: newWarehouse.contact_person.trim(),
        contact_number: newWarehouse.contact_number.trim(),
        contact_email: newWarehouse.contact_email.trim(),
        gstin: newWarehouse.gstin.trim(),
        is_default: !!newWarehouse.is_default,
        address: {
          // Include address id for in-place updates (backend will use it if it matches current address)
          id: newWarehouse.address.id || null,
          line1: newWarehouse.address.line1.trim(),
          line2: newWarehouse.address.line2?.trim() || '',
          city: newWarehouse.address.city.trim(),
          state: newWarehouse.address.state.trim(),
          pincode: String(newWarehouse.address.pincode).trim(),
          country: newWarehouse.address.country?.trim() || 'India',
          phone: newWarehouse.address.phone?.trim() || newWarehouse.contact_number.trim(),
          landmark: newWarehouse.address.landmark?.trim() || '',
        },
      };

      if (editingWarehouseId) {
        await updateVendorPickupAddress(vendor.id, editingWarehouseId, payload);
      } else {
        await createVendorPickupAddress(vendor.id, payload);
      }

      setShowAddWarehouseModal(false);
      setEditingWarehouseId(null);
      setNewWarehouse(getEmptyWarehouseForm());
      setWarehouseErrors({});
      fetchWarehouses();
    } catch (err) {
      console.error("Error adding warehouse:", err);
      showToast({ type: 'error', message: `Failed to add warehouse: ${err.message || 'Please try again.'}` });
    }
  };

  const CONTACT_TYPES = [
    { value: "primary", label: "Primary Contact" },
    { value: "billing", label: "Billing Contact" },
    { value: "technical", label: "Technical Contact" },
    { value: "operations", label: "Operations Contact" },
    { value: "support", label: "Support Contact" },
  ];

  const handleContactInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewContact((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setContactErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateContactForm = (data) => {
    const errors = {};
    const trim = (v = "") => String(v).trim();

    if (!trim(data.name)) errors.name = "Name is required";
    if (!trim(data.contact_type)) errors.contact_type = "Type is required";
    if (!trim(data.email)) errors.email = "Email is required";

    return errors;
  };

  const resetContactForm = () => {
    setEditingContactId(null);
    setNewContact({
      name: "",
      contact_type: "primary",
      email: "",
      phone: "",
      designation: "",
      is_primary: false,
      is_active: true,
      notes: "",
    });
    setContactErrors({});
  };

  const handleSaveContact = async (e) => {
    e.preventDefault();
    try {
      const errors = validateContactForm(newContact);
      setContactErrors(errors);
      if (Object.keys(errors).length > 0) {
        const firstError = Object.values(errors)[0];
        showToast({ type: "error", message: firstError });
        return;
      }

      const payload = {
        name: newContact.name.trim(),
        contact_type: newContact.contact_type,
        email: newContact.email.trim(),
        phone: newContact.phone?.trim() || "",
        designation: newContact.designation?.trim() || "",
        is_primary: !!newContact.is_primary,
        is_active: !!newContact.is_active,
        notes: newContact.notes || "",
      };

      if (editingContactId) {
        await updateVendorContact(vendor.id, editingContactId, payload, "PATCH");
        showToast({ type: "success", message: "Contact updated successfully." });
      } else {
        await createVendorContact(vendor.id, payload);
        showToast({ type: "success", message: "Contact added successfully." });
      }

      setShowContactModal(false);
      resetContactForm();
      await fetchContacts();
    } catch (err) {
      console.error("Error saving contact:", err);
      showToast({
        type: "error",
        message: `Failed to save contact: ${err.message || "Please try again."}`,
      });
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    try {
      await deleteVendorContact(vendor.id, contactId);
      showToast({ type: "success", message: "Contact deleted successfully." });
      await fetchContacts();
    } catch (err) {
      console.error("Error deleting contact:", err);
      showToast({
        type: "error",
        message: `Failed to delete contact: ${err.message || "Please try again."}`,
      });
    }
  };

  const handleDeleteWarehouse = async (warehouseId) => {
    if (!window.confirm("Are you sure you want to delete this warehouse?")) return;
    try {
      await deleteVendorPickupAddress(vendor.id, warehouseId);
      fetchWarehouses();
    } catch (err) {
      console.error("Error deleting warehouse:", err);
      showToast({ type: 'error', message: `Failed to delete warehouse: ${err.message || 'Please try again.'}` });
    }
  };

  const handleToggleDefault = async (warehouseId) => {
    try {
      await updateVendorPickupAddress(vendor.id, warehouseId, { is_default: true });
      fetchWarehouses();
    } catch (err) {
      console.error("Error setting default warehouse:", err);
      showToast({ type: 'error', message: `Failed to set default warehouse: ${err.message || 'Please try again.'}` });
    }
  };

  const renderWarehouses = () => {
    const term = warehouseSearchTerm.toLowerCase();
    const filteredWarehouses = warehouses.filter((wh) => {
      const address = wh.address || wh.address_detail || {};
      const addressStr = [
        address.address_line_1,
        address.address_line_2,
        address.city,
        address.state,
        address.pincode,
        address.country,
        address.landmark,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const haystack = [
        wh.warehouse_name,
        wh.contact_person,
        wh.contact_number,
        wh.contact_email,
        wh.gstin,
        addressStr,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });

    // Calculate selection state for header checkbox
    const pageWarehouseIds = filteredWarehouses.map(wh => wh.id);
    const selectedOnPage = selectedWarehouses.filter(id => pageWarehouseIds.includes(id));
    const isAllSelected = pageWarehouseIds.length > 0 && selectedOnPage.length === pageWarehouseIds.length;
    const isPartiallySelected = selectedOnPage.length > 0 && selectedOnPage.length < pageWarehouseIds.length;

    const handleSelectAll = (checked) => {
      if (checked) {
        setSelectedWarehouses(filteredWarehouses.map(wh => wh.id));
      } else {
        setSelectedWarehouses([]);
      }
    };

    const handleSelectWarehouse = (warehouseId) => {
      setSelectedWarehouses(prev =>
        prev.includes(warehouseId)
          ? prev.filter(id => id !== warehouseId)
          : [...prev, warehouseId]
      );
    };

    return (
      <div className="tabContent">
        <div className="warehousesContainer">
          {/* Search and Add Section */}
          <div className="warehousesHeader">
            <div className="warehouseSearchContainer">
              <FaSearch className="searchIcon" />
              <input
                type="text"
                className="warehouseSearchInput"
                placeholder="Search by warehouse name"
                value={warehouseSearchTerm}
                onChange={(e) => setWarehouseSearchTerm(e.target.value)}
              />
            </div>
            <div className="warehousesActions">
              <button
                className="addWarehouseBtn"
                onClick={() => {
                  setEditingWarehouseId(null);
                  setNewWarehouse(getEmptyWarehouseForm());
                  setShowAddWarehouseModal(true);
                }}
              >
                <FaPlus /> Add Warehouse
              </button>
              <button className="moreOptionsBtn" title="More options">
                <FaEllipsisV />
              </button>
            </div>
          </div>

          {/* Warehouses Table */}
          {loadingWarehouses ? (
            <div className="loadingContainer">
              <p>Loading warehouses...</p>
            </div>
          ) : (
            <table className="warehousesTable">
              <thead>
                <tr>
                  <th className="checkboxColumn">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={input => {
                        if (input) {
                          input.indeterminate = isPartiallySelected;
                        }
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th>Warehouse Name</th>
                  <th>Warehouse Address</th>
                  <th>Contact Person</th>
                  <th>Contact Number</th>
                  <th>Contact Email</th>
                  <th>GSTIN</th>
                  <th>Is Default</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWarehouses.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="noRecordsCell">
                      No matching records
                    </td>
                  </tr>
                ) : (
                  filteredWarehouses.map((warehouse) => (
                    <tr key={warehouse.id}>
                      <td className="checkboxColumn">
                        <input
                          type="checkbox"
                          checked={selectedWarehouses.includes(warehouse.id)}
                          onChange={() => handleSelectWarehouse(warehouse.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>{warehouse.warehouse_name || "—"}</td>
                      <td>
                        {(() => {
                          const address = warehouse.address || {};
                          const addressLine = [
                            address.address_line_1,
                            address.address_line_2,
                            address.city,
                            address.state,
                            address.pincode,
                            address.country,
                          ]
                            .filter(Boolean)
                            .join(', ');
                          return addressLine || '—';
                        })()}
                      </td>
                      <td>{warehouse.contact_person || "—"}</td>
                      <td>{warehouse.contact_number || "—"}</td>
                      <td>{warehouse.contact_email || "—"}</td>
                      <td>{warehouse.gstin || "—"}</td>
                      <td>
                        {warehouse.is_default ? (
                          <span className="defaultBadge">Default</span>
                        ) : (
                          <button
                            className="setDefaultBtn"
                            onClick={() => handleToggleDefault(warehouse.id)}
                          >
                            Set as Default
                          </button>
                        )}
                      </td>
                      <td>
                        <div className="warehouseActions">
                          <button
                            className="actionIconBtn"
                            title="Edit"
                            onClick={() => {
                              const address = warehouse.address || {};
                              setEditingWarehouseId(warehouse.id);
                              setNewWarehouse({
                                warehouse_name: warehouse.warehouse_name || '',
                                contact_person: warehouse.contact_person || '',
                                contact_number: warehouse.contact_number || '',
                                contact_email: warehouse.contact_email || '',
                                gstin: warehouse.gstin || '',
                                is_default: !!warehouse.is_default,
                                address: {
                                  id: address.id || address.address_id || null,
                                  line1: address.address_line_1 || address.line1 || '',
                                  line2: address.address_line_2 || address.line2 || '',
                                  city: address.city || '',
                                  state: address.state || '',
                                  pincode: address.pincode || '',
                                  country: address.country || 'India',
                                  phone: address.phone || warehouse.contact_number || '',
                                  landmark: address.landmark || '',
                                },
                              });
                              setShowAddWarehouseModal(true);
                            }}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="actionIconBtn deleteBtn"
                            title="Delete"
                            onClick={() => handleDeleteWarehouse(warehouse.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Add Warehouse Modal */}
        {showAddWarehouseModal && (
          <div className="modalOverlay" onClick={() => {
            setShowAddWarehouseModal(false);
            setEditingWarehouseId(null);
            setNewWarehouse(getEmptyWarehouseForm());
            setWarehouseErrors({});
          }}>
            <div className="modalContent" onClick={(e) => e.stopPropagation()}>
              <h3>{editingWarehouseId ? 'Edit Warehouse' : 'Add Warehouse'}</h3>
              <form className="addVendorForm" onSubmit={handleAddWarehouse}>
                <div className="formGrid">
                  <div className="formGroup">
                    <label>Warehouse Name <span className="required">*</span></label>
                    <input
                      type="text"
                      name="warehouse_name"
                      value={newWarehouse.warehouse_name}
                      onChange={handleWarehouseInputChange}
                      className={`formInput ${warehouseErrors.warehouse_name ? 'error' : ''}`}
                      required
                    />
                    {warehouseErrors.warehouse_name && <span className="errorText">{warehouseErrors.warehouse_name}</span>}
                  </div>
                  <div className="formGroup">
                    <label>Contact Person <span className="required">*</span></label>
                    <input
                      type="text"
                      name="contact_person"
                      value={newWarehouse.contact_person}
                      onChange={handleWarehouseInputChange}
                      className={`formInput ${warehouseErrors.contact_person ? 'error' : ''}`}
                      required
                    />
                    {warehouseErrors.contact_person && <span className="errorText">{warehouseErrors.contact_person}</span>}
                  </div>
                  <div className="formGroup">
                    <label>Contact Number <span className="required">*</span></label>
                    <input
                      type="text"
                      name="contact_number"
                      value={newWarehouse.contact_number}
                      onChange={handleWarehouseInputChange}
                      className={`formInput ${warehouseErrors.contact_number ? 'error' : ''}`}
                      inputMode="tel"
                      required
                    />
                    {warehouseErrors.contact_number && <span className="errorText">{warehouseErrors.contact_number}</span>}
                  </div>
                  <div className="formGroup">
                    <label>Contact Email <span className="required">*</span></label>
                    <input
                      type="email"
                      name="contact_email"
                      value={newWarehouse.contact_email}
                      onChange={handleWarehouseInputChange}
                      className={`formInput ${warehouseErrors.contact_email ? 'error' : ''}`}
                      required
                    />
                    {warehouseErrors.contact_email && <span className="errorText">{warehouseErrors.contact_email}</span>}
                  </div>
                  <div className="formGroup">
                    <label>GSTIN <span className="required">*</span></label>
                    <input
                      type="text"
                      name="gstin"
                      value={newWarehouse.gstin}
                      onChange={handleWarehouseInputChange}
                      className={`formInput ${warehouseErrors.gstin ? 'error' : ''}`}
                      required
                    />
                    {warehouseErrors.gstin && <span className="errorText">{warehouseErrors.gstin}</span>}
                  </div>

                  <div className="formGroup">
                    <label>Address Line 1 <span className="required">*</span></label>
                    <input
                      type="text"
                      name="line1"
                      value={newWarehouse.address.line1}
                      onChange={handleWarehouseInputChange}
                      className={`formInput ${warehouseErrors.line1 ? 'error' : ''}`}
                      required
                    />
                    {warehouseErrors.line1 && <span className="errorText">{warehouseErrors.line1}</span>}
                  </div>
                  <div className="formGroup">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      name="line2"
                      value={newWarehouse.address.line2}
                      onChange={handleWarehouseInputChange}
                      className="formInput"
                    />
                  </div>
                  <div className="formGroup">
                    <label>City <span className="required">*</span></label>
                    <input
                      type="text"
                      name="city"
                      value={newWarehouse.address.city}
                      onChange={handleWarehouseInputChange}
                      className={`formInput ${warehouseErrors.city ? 'error' : ''}`}
                      required
                    />
                    {warehouseErrors.city && <span className="errorText">{warehouseErrors.city}</span>}
                  </div>
                  <div className="formGroup">
                    <label>State <span className="required">*</span></label>
                    <input
                      type="text"
                      name="state"
                      value={newWarehouse.address.state}
                      onChange={handleWarehouseInputChange}
                      className={`formInput ${warehouseErrors.state ? 'error' : ''}`}
                      required
                    />
                    {warehouseErrors.state && <span className="errorText">{warehouseErrors.state}</span>}
                  </div>
                  <div className="formGroup">
                    <label>Pincode <span className="required">*</span></label>
                    <input
                      type="text"
                      name="pincode"
                      value={newWarehouse.address.pincode}
                      onChange={handleWarehouseInputChange}
                      className={`formInput ${warehouseErrors.pincode ? 'error' : ''}`}
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      required
                    />
                    {warehouseErrors.pincode && <span className="errorText">{warehouseErrors.pincode}</span>}
                  </div>
                  <div className="formGroup">
                    <label>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={newWarehouse.address.country}
                      onChange={handleWarehouseInputChange}
                      className="formInput"
                    />
                  </div>
                  <div className="formGroup">
                    <label>Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={newWarehouse.address.phone}
                      onChange={handleWarehouseInputChange}
                      className="formInput"
                      inputMode="tel"
                    />
                  </div>
                  <div className="formGroup">
                    <label>Landmark</label>
                    <input
                      type="text"
                      name="landmark"
                      value={newWarehouse.address.landmark}
                      onChange={handleWarehouseInputChange}
                      className="formInput"
                    />
                  </div>

                  <div className="formGroup">
                    <label className="checkboxLabel">
                      <input
                        type="checkbox"
                        name="is_default"
                        checked={newWarehouse.is_default}
                        onChange={handleWarehouseInputChange}
                      />
                      Set as Default Warehouse
                    </label>
                  </div>
                </div>
                <div className="formActions">
                  <button type="submit" className="createBtn">{editingWarehouseId ? 'Save' : 'Add Warehouse'}</button>
                  <button
                    type="button"
                    className="cancelBtn"
                    onClick={() => {
                      setShowAddWarehouseModal(false);
                      setEditingWarehouseId(null);
                      setNewWarehouse(getEmptyWarehouseForm());
                      setWarehouseErrors({});
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContactDetails = () => {
    const term = contactSearchTerm.toLowerCase();
    const filteredContacts = contacts.filter((c) => {
      const haystack = [
        c.name,
        c.contact_type,
        c.email,
        c.phone,
        c.designation,
        c.notes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });

    const pageContactIds = filteredContacts.map((c) => c.id);
    const selectedOnPage = selectedContacts.filter((id) => pageContactIds.includes(id));
    const isAllSelected =
      pageContactIds.length > 0 && selectedOnPage.length === pageContactIds.length;
    const isPartiallySelected =
      selectedOnPage.length > 0 && selectedOnPage.length < pageContactIds.length;

    const handleSelectAll = (checked) => {
      if (checked) {
        setSelectedContacts(filteredContacts.map((c) => c.id));
      } else {
        setSelectedContacts([]);
      }
    };

    const handleSelectContact = (contactId) => {
      setSelectedContacts((prev) =>
        prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
      );
    };

    return (
      <div className="tabContent">
        <div className="warehousesContainer">
          <div className="warehousesHeader">
            <div className="warehouseSearchContainer">
              <FaSearch className="searchIcon" />
              <input
                type="text"
                className="warehouseSearchInput"
                placeholder="Search by name, email, or type"
                value={contactSearchTerm}
                onChange={(e) => setContactSearchTerm(e.target.value)}
              />
            </div>
            <div className="warehousesActions">
              <button
                className="addWarehouseBtn"
                onClick={() => {
                  setEditingContactId(null);
                  resetContactForm();
                  setShowContactModal(true);
                }}
              >
                <FaPlus /> Add Contact
              </button>
            </div>
          </div>

          {loadingContacts ? (
            <div className="loadingContainer">
              <p>Loading contacts...</p>
            </div>
          ) : (
            <table className="warehousesTable">
              <thead>
                <tr>
                  <th className="checkboxColumn">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = isPartiallySelected;
                        }
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Designation</th>
                  <th>Primary</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: "16px 0" }}>
                      No contacts found.
                    </td>
                  </tr>
                ) : (
                  filteredContacts.map((contact) => (
                    <tr key={contact.id}>
                      <td className="checkboxColumn">
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={() => handleSelectContact(contact.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>{contact.name || "—"}</td>
                      <td>{contact.contact_type || "—"}</td>
                      <td>{contact.email || "—"}</td>
                      <td>{contact.phone || "—"}</td>
                      <td>{contact.designation || "—"}</td>
                      <td>{contact.is_primary ? "Yes" : "No"}</td>
                      <td>{contact.is_active ? "Yes" : "No"}</td>
                      <td>
                        <div className="warehouseActions">
                          <button
                            type="button"
                            className="iconButton"
                            onClick={() => {
                              setEditingContactId(contact.id);
                              setNewContact({
                                name: contact.name || "",
                                contact_type: contact.contact_type || "primary",
                                email: contact.email || "",
                                phone: contact.phone || "",
                                designation: contact.designation || "",
                                is_primary: !!contact.is_primary,
                                is_active: contact.is_active !== false,
                                notes: contact.notes || "",
                              });
                              setContactErrors({});
                              setShowContactModal(true);
                            }}
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            className="iconButton delete"
                            onClick={() => handleDeleteContact(contact.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* Add / Edit Contact Modal */}
          {showContactModal && (
            <div
              className="modalOverlay"
              onClick={() => {
                setShowContactModal(false);
                resetContactForm();
              }}
            >
              <div className="modalContent" onClick={(e) => e.stopPropagation()}>
                <h3>{editingContactId ? "Edit Contact" : "Add Contact"}</h3>
                <form className="addVendorForm" onSubmit={handleSaveContact}>
                  <div className="formGrid">
                    <div className="formGroup">
                      <label>Name <span className="required">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={newContact.name}
                        onChange={handleContactInputChange}
                        className={`formInput ${contactErrors.name ? "error" : ""}`}
                        required
                      />
                      {contactErrors.name && (
                        <span className="errorText">{contactErrors.name}</span>
                      )}
                    </div>
                    <div className="formGroup">
                      <label>Type <span className="required">*</span></label>
                      <select
                        name="contact_type"
                        value={newContact.contact_type}
                        onChange={handleContactInputChange}
                        className={`formInput ${contactErrors.contact_type ? "error" : ""}`}
                        required
                      >
                        {CONTACT_TYPES.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {contactErrors.contact_type && (
                        <span className="errorText">{contactErrors.contact_type}</span>
                      )}
                    </div>
                    <div className="formGroup">
                      <label>Email (comma-separated allowed) <span className="required">*</span></label>
                      <input
                        type="text"
                        name="email"
                        value={newContact.email}
                        onChange={handleContactInputChange}
                        className={`formInput ${contactErrors.email ? "error" : ""}`}
                        required
                      />
                      {contactErrors.email && (
                        <span className="errorText">{contactErrors.email}</span>
                      )}
                    </div>
                    <div className="formGroup">
                      <label>Phone</label>
                      <input
                        type="text"
                        name="phone"
                        value={newContact.phone}
                        onChange={handleContactInputChange}
                        className="formInput"
                      />
                    </div>
                    <div className="formGroup">
                      <label>Designation</label>
                      <input
                        type="text"
                        name="designation"
                        value={newContact.designation}
                        onChange={handleContactInputChange}
                        className="formInput"
                      />
                    </div>
                    <div className="formGroup">
                      <label>Notes</label>
                      <textarea
                        name="notes"
                        value={newContact.notes}
                        onChange={handleContactInputChange}
                        className="formInput"
                        rows={3}
                      />
                    </div>
                    <div className="formGroup">
                      <label className="checkboxLabel">
                        <input
                          type="checkbox"
                          name="is_primary"
                          checked={newContact.is_primary}
                          onChange={handleContactInputChange}
                        />
                        Set as Primary Contact
                      </label>
                    </div>
                    <div className="formGroup">
                      <label className="checkboxLabel">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={newContact.is_active}
                          onChange={handleContactInputChange}
                        />
                        Active
                      </label>
                    </div>
                  </div>
                  <div className="formActions">
                    <button type="submit" className="createBtn">
                      {editingContactId ? "Save Contact" : "Add Contact"}
                    </button>
                    <button
                      type="button"
                      className="cancelBtn"
                      onClick={() => {
                        setShowContactModal(false);
                        resetContactForm();
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleAddShippingPartner = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!newShippingPartner.shipper_company || !newShippingPartner.nickname) {
      showToast({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }

    try {
      const payload = {
        shipper_company: newShippingPartner.shipper_company,
        nickname: newShippingPartner.nickname,
        ...(Object.keys(newShippingPartner.credentials).length > 0 && { credentials: newShippingPartner.credentials }),
      };

      console.log("Adding shipping partner with payload:", payload);
      console.log("API URL:", `${API_BASE_URL}/${vendor.id}/shipping-partners/add/`);

      // Try different possible endpoint formats
      let response;
      const possibleEndpoints = [
        `${API_BASE_URL}/${vendor.id}/shipping-partners/add/`,
        `${API_BASE_URL}/${vendor.id}/shipping-partners/`,
      ];

      let lastError;
      for (const endpoint of possibleEndpoints) {
        try {
          console.log("Trying endpoint:", endpoint);
          const fetchResponse = await fetch(endpoint, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload),
          });

          if (!fetchResponse.ok) {
            if (fetchResponse.status !== 404) {
              const errorData = await fetchResponse.json().catch(() => ({}));
              throw new Error(errorData.message || errorData.detail || `HTTP ${fetchResponse.status}`);
            }
            throw new Error(`HTTP ${fetchResponse.status}`);
          }

          response = await fetchResponse.json();
          console.log("Success with endpoint:", endpoint);
          break;
        } catch (err) {
          lastError = err;
          // If it's not a 404, stop trying other endpoints
          if (err.message && !err.message.includes('404')) {
            throw err;
          }
          console.log("Endpoint not found, trying next...");
        }
      }

      // If all endpoints failed with 404, throw the last error
      if (!response && lastError) {
        throw lastError;
      }

      console.log("Shipping partner added successfully:", response);

      // Success - close modal and refresh list
      setShowAddShippingPartnerModal(false);
      setNewShippingPartner({ shipper_company: "", nickname: "", credentials: {} });
      setShipperCompanySearch("");
      setShowShipperDropdown(false);
      fetchShippingPartners();

      // Show success message
      showToast({ type: 'success', message: 'Shipping partner added successfully!' });
    } catch (err) {
      console.error("Error adding shipping partner:", err);
      console.error("Full error object:", err);

      // Provide more detailed error message
      let errorMessage = "Failed to add shipping partner.\n\n";

      // Check if error message contains status code
      const statusMatch = err.message?.match(/HTTP (\d+)/);
      const status = statusMatch ? parseInt(statusMatch[1]) : null;

      if (status === 404) {
        errorMessage += "⚠️ API endpoint not found (404).\n\n";
        errorMessage += "The backend endpoint for adding shipping partners may not be implemented yet.\n\n";
        errorMessage += "For now, the shipping partner will be stored locally.\n";
        errorMessage += "Please implement the endpoint:\n";
        errorMessage += `${API_BASE_URL}/${vendor.id}/shipping-partners/add/`;

        // Store locally as fallback
        const localPartners = JSON.parse(localStorage.getItem(`shipping_partners_${vendor.id}`) || '[]');
        const newPartner = {
          id: Date.now(),
          shipper_company: newShippingPartner.shipper_company,
          nickname: newShippingPartner.nickname,
          partner: newShippingPartner.shipper_company.toLowerCase().replace(/\s+/g, '-'),
          is_default: false,
          status: ["Pending"],
          created_at: new Date().toISOString(),
        };
        localPartners.push(newPartner);
        localStorage.setItem(`shipping_partners_${vendor.id}`, JSON.stringify(localPartners));

        // Update state
        setShippingPartners([...shippingPartners, newPartner]);
        setShowAddShippingPartnerModal(false);
        setNewShippingPartner({ shipper_company: "", nickname: "", credentials: {} });
        setShipperCompanySearch("");
        setShowShipperDropdown(false);

        showToast({ type: 'success', message: 'Shipping partner saved locally (backend endpoint not available).' });
        return;
      } else if (status === 401 || status === 403) {
        errorMessage += "🔒 Authentication failed. Please log in again.";
      } else if (status === 400) {
        errorMessage += "❌ Validation Error:\n";
        errorMessage += `  ${err.message || "Invalid request data."}`;
      } else if (status && status >= 500) {
        errorMessage += "🔴 Server error (500+). Please try again later.";
        errorMessage += `\n\nDetails: ${err.message}`;
      } else if (status) {
        errorMessage += `❌ Error ${status}: ${err.message || 'Unknown error'}`;
      } else {
        // Network or other error
        errorMessage += "🌐 Network error or server unavailable.\n\n";
        errorMessage += "Possible issues:\n";
        errorMessage += "  • Backend server is not running\n";
        errorMessage += "  • Network connection problem\n";
        errorMessage += "  • CORS configuration issue\n\n";
        errorMessage += `Tried to reach: ${API_BASE_URL}\n`;
        errorMessage += `Error: ${err.message || 'Unknown error'}`;
      }

      // Always show the detailed error
      showToast({ type: 'error', message: errorMessage });
    }
  };

  const ComingSoonPanel = ({ title, icon = '⏳', description }) => (
    <div className="tabContent">
      <div className="comingSoonContainer">
        <div className="comingSoonContent">
          <div className="comingSoonIcon" aria-hidden="true">{icon}</div>
          <h2 className="comingSoonTitle">{title}</h2>
          <p className="comingSoonSubtitle">Coming Soon</p>
          {description ? (
            <p className="comingSoonDescription">{description}</p>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderShippingPartners = () => {
    return (
      <ComingSoonPanel
        title="Shipping Partners"
        icon="🚚"
        description="Shipping partners management is under development. Please check back later."
      />
    );
  };



  const renderUsersPermissions = () => (
    <ComingSoonPanel
      title="Users & Permissions"
      icon="👥"
      description="User and permissions management is under development. Please check back later."
    />
  );

  const validatePaymentForm = () => {
    const errors = {};
    if (!newPaymentIntegration.nickname) errors.nickname = "Nick Name is required";
    if (!newPaymentIntegration.account_name) errors.account_name = "Account Name is required";
    if (!newPaymentIntegration.account_number) errors.account_number = "Account Number is required";
    if (!newPaymentIntegration.bank_name) errors.bank_name = "Bank Name is required";
    if (!newPaymentIntegration.sort_code) errors.sort_code = "Sort Code/IFSC/Routing Number is required";
    setPaymentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddPaymentIntegration = async (e) => {
    e.preventDefault();
    if (!validatePaymentForm()) return;

    // Validate required fields
    if (!newPaymentIntegration.payment_method || !newPaymentIntegration.nickname ||
      !newPaymentIntegration.account_name || !newPaymentIntegration.account_number ||
      !newPaymentIntegration.bank_name || !newPaymentIntegration.sort_code) {
      showToast({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }

    try {
      const payload = {
        payment_gateway: newPaymentIntegration.payment_gateway,
        payment_method: newPaymentIntegration.payment_method,
        nickname: newPaymentIntegration.nickname,
        account_name: newPaymentIntegration.account_name,
        account_number: newPaymentIntegration.account_number,
        bank_name: newPaymentIntegration.bank_name,
        sort_code: newPaymentIntegration.sort_code,
        other_information: newPaymentIntegration.other_information || "",
        is_default: newPaymentIntegration.is_default,
      };

      console.log("Adding payment integration with payload:", payload);

      // Try different possible endpoint formats
      let response;
      const possibleEndpoints = [
        `${API_BASE_URL}/${vendor.id}/payment-integrations/add/`,
        `${API_BASE_URL}/${vendor.id}/payment-providers/add/`,
        `${API_BASE_URL}/${vendor.id}/payment-integrations/`,
      ];

      let lastError;
      for (const endpoint of possibleEndpoints) {
        try {
          console.log("Trying endpoint:", endpoint);
          const fetchResponse = await fetch(endpoint, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload),
          });

          if (!fetchResponse.ok) {
            if (fetchResponse.status !== 404) {
              const errorData = await fetchResponse.json().catch(() => ({}));
              throw new Error(errorData.message || errorData.detail || `HTTP ${fetchResponse.status}`);
            }
            throw new Error(`HTTP ${fetchResponse.status}`);
          }

          response = await fetchResponse.json();
          console.log("Success with endpoint:", endpoint);
          break;
        } catch (err) {
          lastError = err;
          if (err.message && !err.message.includes('404')) {
            throw err;
          }
          console.log("Endpoint not found, trying next...");
        }
      }

      if (!response && lastError) {
        throw lastError;
      }

      console.log("Payment integration added successfully:", response);

      // Success - close modal and refresh list
      setShowAddPaymentModal(false);
      setNewPaymentIntegration({
        payment_gateway: "Manual",
        payment_method: "",
        nickname: "",
        account_name: "",
        account_number: "",
        bank_name: "",
        sort_code: "",
        other_information: "",
        is_default: false,
      });
      setPaymentErrors({});
      fetchPaymentProviders();
      showToast({ type: 'success', message: 'Payment integration added successfully!' });
    } catch (err) {
      console.error("Error adding payment integration:", err);

      // If 404, store locally
      if (err.response?.status === 404) {
        const localIntegrations = JSON.parse(localStorage.getItem(`payment_integrations_${vendor.id}`) || '[]');
        const newIntegration = {
          id: Date.now(),
          provider_name: newPaymentIntegration.nickname,
          payment_method: newPaymentIntegration.payment_method,
          status: "Pending",
          is_default: newPaymentIntegration.is_default,
          ...newPaymentIntegration,
          created_at: new Date().toISOString(),
        };
        localIntegrations.push(newIntegration);
        localStorage.setItem(`payment_integrations_${vendor.id}`, JSON.stringify(localIntegrations));

        setPaymentProviders([...paymentProviders, newIntegration]);
        setShowAddPaymentModal(false);
        setNewPaymentIntegration({
          payment_gateway: "Manual",
          payment_method: "",
          nickname: "",
          account_name: "",
          account_number: "",
          bank_name: "",
          sort_code: "",
          other_information: "",
          is_default: false,
        });
        setPaymentErrors({});
        showToast({ type: 'success', message: 'Payment integration saved locally (backend endpoint not available).' });
        return;
      }

      let errorMessage = "Failed to add payment integration.\n\n";
      const statusMatch = err.message?.match(/HTTP (\d+)/);
      const status = statusMatch ? parseInt(statusMatch[1]) : null;

      if (status) {
        errorMessage += `Error ${status}: ${err.message || 'Unknown error'}`;
      } else {
        errorMessage += err.message || "An unexpected error occurred.";
        if (!err.message || !err.message.includes('Network')) {
          errorMessage += "\n\nPlease check if the backend is running.";
        }
      }

      showToast({ type: 'error', message: errorMessage });
    }
  };

  const renderPaymentIntegration = () => {
    return (
      <div className="tabContent">
        <div className="paymentIntegrationContainer">
          <div className="paymentIntegrationTableWrapper">
            <table className="paymentProvidersTable">
              <thead>
                <tr>
                  <th>Provider Name</th>
                  <th>Status</th>
                  <th className="addButtonColumn">
                    <button
                      className="addPaymentBtn"
                      onClick={() => setShowAddPaymentModal(true)}
                      title="Add Payment Integration"
                    >
                      <FaPlus />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loadingPayment ? (
                  <tr>
                    <td colSpan="3" className="loadingCell">
                      <div className="loadingContainer">
                        <p>Loading payment integrations...</p>
                      </div>
                    </td>
                  </tr>
                ) : paymentProviders.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="noRecordsCell">
                      No matching records
                    </td>
                  </tr>
                ) : (
                  paymentProviders.map((provider) => (
                    <tr key={provider.id}>
                      <td>{provider.provider_name || provider.nickname || provider.name || "—"}</td>
                      <td>
                        <span className={`statusBadge ${provider.status === 'connected' || provider.status === 'Connected' ? 'connected' : provider.status === 'Pending' ? 'pending' : 'disconnected'}`}>
                          {provider.status || 'not connected'}
                        </span>
                      </td>
                      <td></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Payment Integration Modal - Full Page Style */}
        {showAddPaymentModal && (
          <div className="addPaymentIntegrationPage">
            <div className="paymentIntegrationPageHeader">
              <button
                className="backArrowBtn"
                onClick={() => {
                  setShowAddPaymentModal(false);
                  setNewPaymentIntegration({
                    payment_gateway: "Manual",
                    payment_method: "",
                    nickname: "",
                    account_name: "",
                    account_number: "",
                    bank_name: "",
                    sort_code: "",
                    other_information: "",
                    is_default: false,
                  });
                }}
              >
                ←
              </button>
              <h2>Add Payment Integration</h2>
            </div>

            <div className="paymentIntegrationPageContent">
              <form className="paymentIntegrationForm" onSubmit={handleAddPaymentIntegration}>
                <div className="formGroup">
                  <label>
                    Payment Gateway
                  </label>
                  <select
                    className="formInput"
                    value={newPaymentIntegration.payment_gateway}
                    onChange={(e) => setNewPaymentIntegration(prev => ({ ...prev, payment_gateway: e.target.value }))}
                  >
                    <option value="Manual">Manual</option>
                    <option value="Razorpay">Razorpay</option>
                    <option value="Stripe">Stripe</option>
                    <option value="PayPal">PayPal</option>
                    <option value="PayU">PayU</option>
                    <option value="CCAvenue">CCAvenue</option>
                  </select>
                </div>

                <div className="formGroup">
                  <label>
                    Payment Method <span className="required">*</span>
                  </label>
                  <select
                    className="formInput"
                    value={newPaymentIntegration.payment_method}
                    onChange={(e) => setNewPaymentIntegration(prev => ({ ...prev, payment_method: e.target.value }))}
                    required
                  >
                    <option value="">Select payment method</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="UPI">UPI</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Debit Card">Debit Card</option>
                    <option value="Net Banking">Net Banking</option>
                    <option value="Wallet">Wallet</option>
                  </select>
                </div>

                <div className="formGroup">
                  <label>
                    Nick Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`formInput ${paymentErrors.nickname ? 'error' : ''}`}
                    placeholder="Enter Nick Name"
                    value={newPaymentIntegration.nickname}
                    onChange={(e) => {
                      setNewPaymentIntegration(prev => ({ ...prev, nickname: e.target.value }));
                      if (paymentErrors.nickname) setPaymentErrors(prev => ({ ...prev, nickname: '' }));
                    }}
                    required
                  />
                  {paymentErrors.nickname && <span className="errorText">{paymentErrors.nickname}</span>}
                </div>

                <div className="formGridTwo">
                  <div className="formGroup">
                    <label>
                      Account Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className={`formInput ${paymentErrors.account_name ? 'error' : ''}`}
                      placeholder="Enter Account Name"
                      value={newPaymentIntegration.account_name}
                      onChange={(e) => {
                        setNewPaymentIntegration(prev => ({ ...prev, account_name: e.target.value }));
                        if (paymentErrors.account_name) setPaymentErrors(prev => ({ ...prev, account_name: '' }));
                      }}
                      required
                    />
                    {paymentErrors.account_name && <span className="errorText">{paymentErrors.account_name}</span>}
                    <small className="helpText">(should be same with your bank account name)</small>
                  </div>

                  <div className="formGroup">
                    <label>
                      Account Number <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className={`formInput ${paymentErrors.account_number ? 'error' : ''}`}
                      placeholder="Enter Account Number"
                      value={newPaymentIntegration.account_number}
                      onChange={(e) => {
                        setNewPaymentIntegration(prev => ({ ...prev, account_number: e.target.value }));
                        if (paymentErrors.account_number) setPaymentErrors(prev => ({ ...prev, account_number: '' }));
                      }}
                      required
                    />
                    {paymentErrors.account_number && <span className="errorText">{paymentErrors.account_number}</span>}
                  </div>
                </div>

                <div className="formGridTwo">
                  <div className="formGroup">
                    <label>
                      Bank Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className={`formInput ${paymentErrors.bank_name ? 'error' : ''}`}
                      placeholder="Enter Bank Name"
                      value={newPaymentIntegration.bank_name}
                      onChange={(e) => {
                        setNewPaymentIntegration(prev => ({ ...prev, bank_name: e.target.value }));
                        if (paymentErrors.bank_name) setPaymentErrors(prev => ({ ...prev, bank_name: '' }));
                      }}
                      required
                    />
                    {paymentErrors.bank_name && <span className="errorText">{paymentErrors.bank_name}</span>}
                  </div>

                  <div className="formGroup">
                    <label>
                      Sort Code/IFSC Code/Routing number <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className={`formInput ${paymentErrors.sort_code ? 'error' : ''}`}
                      placeholder="Enter Sort Code/IFSC Code/Routing Number"
                      value={newPaymentIntegration.sort_code}
                      onChange={(e) => {
                        setNewPaymentIntegration(prev => ({ ...prev, sort_code: e.target.value }));
                        if (paymentErrors.sort_code) setPaymentErrors(prev => ({ ...prev, sort_code: '' }));
                      }}
                      required
                    />
                    {paymentErrors.sort_code && <span className="errorText">{paymentErrors.sort_code}</span>}
                  </div>
                </div>

                <div className="formGroup">
                  <label>
                    Other Information
                  </label>
                  <textarea
                    className="formInput"
                    placeholder="Other Information"
                    rows="4"
                    value={newPaymentIntegration.other_information}
                    onChange={(e) => setNewPaymentIntegration(prev => ({ ...prev, other_information: e.target.value }))}
                  />
                </div>

                <div className="formGroup">
                  <label className="checkboxLabel">
                    <input
                      type="checkbox"
                      checked={newPaymentIntegration.is_default}
                      onChange={(e) => setNewPaymentIntegration(prev => ({ ...prev, is_default: e.target.checked }))}
                    />
                    Check to make this payment integration as default
                  </label>
                </div>

                <div className="formActions">
                  <button type="submit" className="saveButton">SAVE</button>
                  <button
                    type="button"
                    className="cancelBtn"
                    onClick={() => {
                      setShowAddPaymentModal(false);
                      setNewPaymentIntegration({
                        payment_gateway: "Manual",
                        payment_method: "",
                        nickname: "",
                        account_name: "",
                        account_number: "",
                        bank_name: "",
                        sort_code: "",
                        other_information: "",
                        is_default: false,
                      });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleCustomSettingsChange = (field, value) => {
    setCustomSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductFieldChange = (field, key, value) => {
    setCustomSettings(prev => ({
      ...prev,
      productFields: {
        ...prev.productFields,
        [field]: {
          ...prev.productFields[field],
          [key]: value
        }
      }
    }));
  };

  const handleSaveCustomSettings = async () => {
    try {
      // TODO: Implement API call to save custom settings
      console.log("Saving custom settings:", customSettings);
      showToast({ type: 'success', message: 'Custom vendor settings saved successfully!' });
    } catch (err) {
      console.error("Error saving custom settings:", err);
      showToast({ type: 'error', message: 'Failed to save custom settings. Please try again.' });
    }
  };

  const handleRemoveWebhook = async () => {
    if (!vendor?.id || !webhookTypeToRemove) return;

    try {
      setRemovingWebhook(true);
      await deregisterWebhookByType(vendor.id, webhookTypeToRemove);

      const webhookLabel = webhookTypeToRemove === 'product' ? 'Product update' : 'Order update';
      showToast({ type: 'success', message: `${webhookLabel} webhook removed successfully.` });
      setShowRemoveWebhookModal(false);

      // Remove the webhook from the local state based on type
      setWebhooks(prev => prev.filter(w => {
        if (webhookTypeToRemove === 'product') {
          return !(w.topic === 'PRODUCTS_UPDATE' || w.topic === 'products/update');
        } else {
          return !(w.topic === 'ORDERS_UPDATED' || w.topic === 'orders/updated' ||
            w.topic === 'orders/create' || w.topic === 'ORDERS_CREATE');
        }
      }));

      // Reset the webhook type
      setWebhookTypeToRemove(null);

      // Refresh config to update UI
      fetchShopifyConfig();
    } catch (err) {
      console.error("Error removing webhook:", err);
      const webhookLabel = webhookTypeToRemove === 'product' ? 'Product update' : 'Order update';
      const errorMessage = err.message || `Failed to remove ${webhookLabel.toLowerCase()} webhook. Please try again.`;
      showToast({ type: 'error', message: errorMessage });
      setShowRemoveWebhookModal(false);
      setWebhookTypeToRemove(null);
    } finally {
      setRemovingWebhook(false);
    }
  };

  const handleRemoveProductSync = async () => {
    if (!vendor?.id) return;

    try {
      setRemovingSync(true);
      await deregisterWebhook(vendor.id);
      showToast({ type: 'success', message: 'Product update sync has been removed. Products remain live.' });
      setShowRemoveSyncModal(false);

      // Refresh config to update UI
      fetchShopifyConfig();
    } catch (err) {
      console.error("Error removing product sync:", err);
      const errorMessage = err.message || 'Failed to remove product sync. Please try again.';
      showToast({ type: 'error', message: errorMessage });
    } finally {
      setRemovingSync(false);
    }
  };

  const renderCustomSettings = () => {
    const productFieldsList = [
      { key: 'product_price', label: 'Product Price' },
      { key: 'title', label: 'Title' },
      { key: 'description', label: 'Description' },
      { key: 'media', label: 'Media' },
    ];

    return (
      <div className="customSettingsContainer">
        <div className="customSettingsSubNav">
          <button
            className={`customSettingsSubNavBtn ${customSettingsSubTab === 'product-settings' ? 'active' : ''}`}
            onClick={() => setCustomSettingsSubTab('product-settings')}
          >
            Product Settings
          </button>
          <button
            className={`customSettingsSubNavBtn ${customSettingsSubTab === 'order-settings' ? 'active' : ''}`}
            onClick={() => setCustomSettingsSubTab('order-settings')}
          >
            Order Settings
          </button>
          <button
            className={`customSettingsSubNavBtn ${customSettingsSubTab === 'vendor-sync' ? 'active' : ''}`}
            onClick={() => setCustomSettingsSubTab('vendor-sync')}
          >
            Vendor Sync Settings
          </button>
          <button
            className={`customSettingsSubNavBtn ${customSettingsSubTab === 'shipping-settings' ? 'active' : ''}`}
            onClick={() => setCustomSettingsSubTab('shipping-settings')}
          >
            Shipping Settings
          </button>
          <button
            className={`customSettingsSubNavBtn ${customSettingsSubTab === 'billing-invoice' ? 'active' : ''}`}
            onClick={() => setCustomSettingsSubTab('billing-invoice')}
          >
            Billing and Invoice
          </button>
          <button
            className={`customSettingsSubNavBtn ${customSettingsSubTab === 'commission' ? 'active' : ''}`}
            onClick={() => setCustomSettingsSubTab('commission')}
          >
            Commission
          </button>
        </div>

        <div className="customSettingsContent">
          {customSettingsSubTab === 'product-settings' && (
            <div className="customSettingsSection">
              <h3>Product Settings</h3>
              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.overrideProductSettings}
                    onChange={(e) => handleCustomSettingsChange('overrideProductSettings', e.target.checked)}
                  />
                  <span>Override Product Settings</span>
                </label>
                <p className="helpText">Ticking any of the boxes will override the Global settings and customize it for the vendor.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.autoApproveProduct}
                    onChange={(e) => handleCustomSettingsChange('autoApproveProduct', e.target.checked)}
                  />
                  <span>Auto Approve Product (New Addition and Edits)</span>
                </label>
                <p className="helpText">Automatically approve product additions or edits from vendors, removing the need for merchant approval.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.deleteProduct}
                    onChange={(e) => handleCustomSettingsChange('deleteProduct', e.target.checked)}
                  />
                  <span>Delete Product</span>
                </label>
                <p className="helpText">Give the vendor the right to delete products from both the app and your store.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.controlProductData}
                    onChange={(e) => handleCustomSettingsChange('controlProductData', e.target.checked)}
                  />
                  <span>Control how vendors access product data</span>
                </label>
                <p className="helpText">View: Selected fields are visible to vendors</p>
                <p className="helpText">Requires Approval: Selected fields require approval on vendor edit</p>
              </div>

              {customSettings.controlProductData && (
                <div className="productFieldsTable">
                  <table className="warehousesTable">
                    <thead>
                      <tr>
                        <th>Product Fields</th>
                        <th>View</th>
                        <th>Requires Approval</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productFieldsList.map(field => (
                        <tr key={field.key}>
                          <td>{field.label}</td>
                          <td>
                            <input
                              type="checkbox"
                              checked={customSettings.productFields[field.key]?.view || false}
                              onChange={(e) => handleProductFieldChange(field.key, 'view', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={customSettings.productFields[field.key]?.requiresApproval || false}
                              onChange={(e) => handleProductFieldChange(field.key, 'requiresApproval', e.target.checked)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="formActions">
                <button type="button" className="saveButton" onClick={handleSaveCustomSettings}>
                  SAVE
                </button>
              </div>
            </div>
          )}

          {customSettingsSubTab === 'order-settings' && (
            <div className="customSettingsSection">
              <h3>Order Settings</h3>
              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.overrideOrderRouting}
                    onChange={(e) => handleCustomSettingsChange('overrideOrderRouting', e.target.checked)}
                  />
                  <span>Override Order Routing Settings</span>
                </label>
                <p className="helpText">Ticking any of the boxes will override the Global settings and customize it for the vendor.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.routeToMerchantWarehouse}
                    onChange={(e) => handleCustomSettingsChange('routeToMerchantWarehouse', e.target.checked)}
                  />
                  <span>Route vendor orders to merchant warehouse</span>
                </label>
                <p className="helpText">When enabled, all vendor orders will be routed to the merchant's warehouse instead of the vendor's warehouse.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.orderRoutingSettings}
                    onChange={(e) => handleCustomSettingsChange('orderRoutingSettings', e.target.checked)}
                  />
                  <span>Order Routing Settings</span>
                </label>
                {customSettings.orderRoutingSettings && (
                  <div className="nestedCheckboxes">
                    <label className="checkboxLabel">
                      <input
                        type="checkbox"
                        checked={customSettings.requireOrderConfirmation}
                        onChange={(e) => handleCustomSettingsChange('requireOrderConfirmation', e.target.checked)}
                      />
                      <span>Require order confirmation before displaying orders on the vendor dashboard</span>
                    </label>
                    <label className="checkboxLabel">
                      <input
                        type="checkbox"
                        checked={customSettings.correctAllErrors}
                        onChange={(e) => handleCustomSettingsChange('correctAllErrors', e.target.checked)}
                      />
                      <span>Correct all errors in order (Product errors, Missing vendor/tax, Address errors)</span>
                    </label>
                    <label className="checkboxLabel">
                      <input
                        type="checkbox"
                        checked={customSettings.generateShippingLabels}
                        onChange={(e) => handleCustomSettingsChange('generateShippingLabels', e.target.checked)}
                      />
                      <span>Generate shipping labels for orders</span>
                    </label>
                    <label className="checkboxLabel">
                      <input
                        type="checkbox"
                        checked={customSettings.generateInvoices}
                        onChange={(e) => handleCustomSettingsChange('generateInvoices', e.target.checked)}
                      />
                      <span>Generate invoices for dropship orders</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="formActions">
                <button type="button" className="saveButton" onClick={handleSaveCustomSettings}>
                  SAVE
                </button>
              </div>
            </div>
          )}

          {customSettingsSubTab === 'vendor-sync' && (
            <div className="customSettingsSection">
              <h3>Vendor Sync Settings</h3>
              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.overrideVendorSync}
                    onChange={(e) => handleCustomSettingsChange('overrideVendorSync', e.target.checked)}
                  />
                  <span>Override Vendor Sync Settings</span>
                </label>
                <p className="helpText">Ticking any of the boxes will override the Global settings and customize it for the vendor.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.allowWebsiteSync}
                    onChange={(e) => handleCustomSettingsChange('allowWebsiteSync', e.target.checked)}
                  />
                  <span>Allow vendors to sync their website</span>
                </label>
                <p className="helpText">Enable vendors to synchronize their website data with the platform.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.enableOrderSync}
                    onChange={(e) => handleCustomSettingsChange('enableOrderSync', e.target.checked)}
                  />
                  <span>Enable order sync</span>
                </label>
                <p className="helpText">Allow vendors to synchronize orders between the platform and their systems.</p>
              </div>

              <div className="formGroup">
                <label>Shopify Store Name</label>
                <input
                  type="text"
                  className="input"
                  value={customSettings.shopify_store_name || ""}
                  onChange={(e) =>
                    handleCustomSettingsChange("shopify_store_name", e.target.value)
                  }
                />
              </div>

              <div className="formGroup">
                <label>Shopify API Key</label>
                <input
                  type="password"
                  className="input"
                  value={customSettings.shopify_api_key || ""}
                  onChange={(e) =>
                    handleCustomSettingsChange("shopify_api_key", e.target.value)
                  }
                />
              </div>


              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.pushOnlyIfPaid}
                    onChange={(e) => handleCustomSettingsChange('pushOnlyIfPaid', e.target.checked)}
                  />
                  <span>Push Only If Order Paid</span>
                </label>
                <p className="helpText">Only push orders to vendors when payment has been confirmed.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.pushUnsyncedProducts}
                    onChange={(e) => handleCustomSettingsChange('pushUnsyncedProducts', e.target.checked)}
                  />
                  <span>Push orders for unsynced Products</span>
                </label>
                <p className="helpText">Allow pushing orders even for products that haven't been synced yet.</p>
              </div>

              <div className="formActions">
                <button type="button" className="saveButton" onClick={handleSaveCustomSettings}>
                  SAVE
                </button>
              </div>
            </div>
          )}

          {customSettingsSubTab === 'shipping-settings' && (
            <div className="customSettingsSection">
              <h3>Shipping Settings</h3>
              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.overrideShippingSettings}
                    onChange={(e) => handleCustomSettingsChange('overrideShippingSettings', e.target.checked)}
                  />
                  <span>Override Shipping Settings</span>
                </label>
                <p className="helpText">Ticking any of the boxes will override the Global settings and customize it for the vendor.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.allowAddShippingPartners}
                    onChange={(e) => handleCustomSettingsChange('allowAddShippingPartners', e.target.checked)}
                  />
                  <span>Allow Vendors to add their shipping partners</span>
                </label>
                <p className="helpText">Enable vendors to add and manage their own shipping partners.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.allowGenerateShippingLabels}
                    onChange={(e) => handleCustomSettingsChange('allowGenerateShippingLabels', e.target.checked)}
                  />
                  <span>Allow vendors to generate shipping labels</span>
                </label>
                <p className="helpText">Give vendors the ability to generate shipping labels for their orders.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.allowChangeWarehouse}
                    onChange={(e) => handleCustomSettingsChange('allowChangeWarehouse', e.target.checked)}
                  />
                  <span>Allow vendors to change order warehouse</span>
                </label>
                <p className="helpText">Enable vendors to modify the warehouse assignment for orders.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.allowChangeShipmentStatus}
                    onChange={(e) => handleCustomSettingsChange('allowChangeShipmentStatus', e.target.checked)}
                  />
                  <span>Allow vendors to change order shipment status</span>
                </label>
                <p className="helpText">Allow vendors to update the shipment status of orders.</p>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.allowEditPackageDimensions}
                    onChange={(e) => handleCustomSettingsChange('allowEditPackageDimensions', e.target.checked)}
                  />
                  <span>Allow vendors to edit order package dimensions</span>
                </label>
                <p className="helpText">Enable vendors to modify package dimensions for shipping calculations.</p>
              </div>

              <div className="formActions">
                <button type="button" className="saveButton" onClick={handleSaveCustomSettings}>
                  SAVE
                </button>
              </div>
            </div>
          )}

          {customSettingsSubTab === 'billing-invoice' && (
            <div className="customSettingsSection">
              <h3>Billing and Invoice</h3>
              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.overrideInvoiceSettings}
                    onChange={(e) => handleCustomSettingsChange('overrideInvoiceSettings', e.target.checked)}
                  />
                  <span>Override Invoice Settings</span>
                </label>
                <p className="helpText">Ticking any of the boxes will override the Global settings and customize it for the vendor.</p>
              </div>

              <div className="formGroup">
                <label>Select your vendor type</label>
                <select
                  className="formInput"
                  value={customSettings.vendorType}
                  onChange={(e) => handleCustomSettingsChange('vendorType', e.target.value)}
                >
                  <option value="Marketplace">Marketplace</option>
                  <option value="Dropship">Dropship</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>

              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.allowGenerateInvoices}
                    onChange={(e) => handleCustomSettingsChange('allowGenerateInvoices', e.target.checked)}
                  />
                  <span>Allow vendors to generate invoices</span>
                </label>
                <p className="helpText">Enable vendors to create and manage invoices for their orders.</p>
              </div>

              <div className="formActions">
                <button type="button" className="saveButton" onClick={handleSaveCustomSettings}>
                  SAVE
                </button>
              </div>
            </div>
          )}

          {customSettingsSubTab === 'commission' && (
            <div className="customSettingsSection">
              <h3>Set a vendor commission rate</h3>
              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.commissionVariable > 0 || customSettings.commissionFixed > 0}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        handleCustomSettingsChange('commissionVariable', 0);
                        handleCustomSettingsChange('commissionFixed', 0);
                      }
                    }}
                  />
                  <span>Set a vendor commission rate</span>
                </label>
                {(customSettings.commissionVariable > 0 || customSettings.commissionFixed > 0) && (
                  <div className="formGridTwo" style={{ marginTop: '1rem' }}>
                    <div className="formGroup">
                      <input
                        type="number"
                        className="formInput"
                        placeholder="0"
                        value={customSettings.commissionVariable}
                        onChange={(e) => handleCustomSettingsChange('commissionVariable', parseFloat(e.target.value) || 0)}
                      />
                      <span style={{ marginLeft: '0.5rem' }}>% Variable</span>
                    </div>
                    <div className="formGroup">
                      <span style={{ marginRight: '0.5rem' }}>+</span>
                      <input
                        type="number"
                        className="formInput"
                        placeholder="0"
                        value={customSettings.commissionFixed}
                        onChange={(e) => handleCustomSettingsChange('commissionFixed', parseFloat(e.target.value) || 0)}
                      />
                      <span style={{ marginLeft: '0.5rem' }}>Rs. Fixed</span>
                    </div>
                  </div>
                )}
              </div>

              <h3>Commission calculated on full price / discounted price</h3>
              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.commissionOnDiscounted}
                    onChange={(e) => handleCustomSettingsChange('commissionOnDiscounted', e.target.checked)}
                  />
                  <span>On Discounted Price</span>
                </label>
                <label className="checkboxLabel" style={{ marginLeft: '2rem' }}>
                  <input
                    type="checkbox"
                    checked={!customSettings.commissionOnDiscounted}
                    onChange={(e) => handleCustomSettingsChange('commissionOnDiscounted', !e.target.checked)}
                  />
                  <span>On Full Price</span>
                </label>
              </div>

              <h3>Discount % shared by marketplace</h3>
              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.discountSharedByMarketplace > 0}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        handleCustomSettingsChange('discountSharedByMarketplace', 0);
                      }
                    }}
                  />
                  <span>Discount % shared by marketplace</span>
                </label>
                {customSettings.discountSharedByMarketplace > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <input
                      type="number"
                      className="formInput"
                      placeholder="0"
                      value={customSettings.discountSharedByMarketplace}
                      onChange={(e) => handleCustomSettingsChange('discountSharedByMarketplace', parseFloat(e.target.value) || 0)}
                      style={{ width: '200px' }}
                    />
                    <span style={{ marginLeft: '0.5rem' }}>%</span>
                  </div>
                )}
              </div>

              <h3>Commission calculated on shipping</h3>
              <div className="formGroup">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={customSettings.commissionOnShipping}
                    onChange={(e) => handleCustomSettingsChange('commissionOnShipping', e.target.checked)}
                  />
                  <span>Commission calculated on shipping</span>
                </label>
                {customSettings.commissionOnShipping && (
                  <div className="formGridTwo" style={{ marginTop: '1rem' }}>
                    <div className="formGroup">
                      <select
                        className="formInput"
                        value={customSettings.commissionOnShippingType}
                        onChange={(e) => handleCustomSettingsChange('commissionOnShippingType', e.target.value)}
                      >
                        <option value="fixed">Fixed</option>
                        <option value="percentage">Percentage</option>
                      </select>
                    </div>
                    <div className="formGroup">
                      <input
                        type="number"
                        className="formInput"
                        placeholder="0"
                        value={customSettings.commissionOnShippingValue}
                        onChange={(e) => handleCustomSettingsChange('commissionOnShippingValue', parseFloat(e.target.value) || 0)}
                      />
                      <span style={{ marginLeft: '0.5rem' }}>
                        {customSettings.commissionOnShippingType === 'fixed' ? 'Rs.' : '%'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="formActions">
                <button type="button" className="saveButton" onClick={handleSaveCustomSettings}>
                  SAVE
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSyncSettings = () => {
    const isProductSection = syncSettingsTab === 'product';
    const fields = isProductSection
      ? [
        { key: 'title', label: 'Title' },
        { key: 'description', label: 'Description' },
        { key: 'product_type', label: 'Product Type' },
        {
          key: 'media',
          label: 'Media'
        },
        { key: 'tags', label: 'Tags' },
      ]
      : [
        { key: 'variant_name', label: 'Variant Name' },
        { key: 'sku', label: 'SKU' },
        { key: 'price', label: 'Price' },
        { key: 'cost_per_item', label: 'Cost per Item' },
        { key: 'compare_price', label: 'Compare at Price' },
        { key: 'weight', label: 'Weight' },
        { key: 'barcode', label: 'Barcode' },
        { key: 'track_quantity', label: 'Track Quantity' },
        { key: 'continue_selling', label: 'Continue Selling When Out of Stock' },
        { key: 'taxable', label: 'Charge Tax on Product' },
      ];

    const handleToggle = async (fieldKey) => {
      const newValue = !syncFieldFilters[fieldKey];
      await autoSaveSyncFilters(fieldKey, newValue);
    };

    return (
      <div className="tabContent">
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
          <div className="edit-card" style={{ padding: '0', borderRadius: '12px', background: 'white', overflow: 'hidden' }}>
            {/* Internal Tab Navigation */}
            <div style={{ display: 'flex', borderBottom: '1px solid #edf2f7', background: '#f8fafc' }}>
              <button
                onClick={() => setSyncSettingsTab('product')}
                style={{
                  padding: '16px 24px',
                  fontWeight: '700',
                  fontSize: '15px',
                  border: 'none',
                  background: 'transparent',
                  color: isProductSection ? '#f97316' : '#64748b',
                  borderBottom: isProductSection ? '3px solid #f97316' : '3px solid transparent',
                  cursor: 'pointer',
                  transition: '0.2s'
                }}
              >
                Product Sync Settings
              </button>
              <button
                onClick={() => setSyncSettingsTab('variant')}
                style={{
                  padding: '16px 24px',
                  fontWeight: '700',
                  fontSize: '15px',
                  border: 'none',
                  background: 'transparent',
                  color: syncSettingsTab === 'variant' ? '#f97316' : '#64748b',
                  borderBottom: syncSettingsTab === 'variant' ? '3px solid #f97316' : '3px solid transparent',
                  cursor: 'pointer',
                  transition: '0.2s'
                }}
              >
                Variant Sync Settings
              </button>
              <button
                onClick={() => setSyncSettingsTab('order')}
                style={{
                  padding: '16px 24px',
                  fontWeight: '700',
                  fontSize: '15px',
                  border: 'none',
                  background: 'transparent',
                  color: syncSettingsTab === 'order' ? '#f97316' : '#64748b',
                  borderBottom: syncSettingsTab === 'order' ? '3px solid #f97316' : '3px solid transparent',
                  cursor: 'pointer',
                  transition: '0.2s'
                }}
              >
                Order Sync Settings
              </button>
            </div>

            <div style={{ padding: '30px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1a202c', marginBottom: '8px' }}>
                {syncSettingsTab === 'product' ? 'Product Field Filters' : syncSettingsTab === 'variant' ? 'Variant Field Filters' : 'Order Sync Settings'}
              </h3>
              <p style={{ fontSize: '14px', color: '#718096', marginBottom: '24px', borderBottom: '1px solid #edf2f7', paddingBottom: '16px' }}>
                {syncSettingsTab === 'order'
                  ? 'Configure how orders are synced between your Shopify store and the vendor.'
                  : `Configure which ${syncSettingsTab === 'product' ? 'product' : 'variant'} fields are synced to Shopify during updates.`}
              </p>

              {syncSettingsTab === 'order' ? (
                loadingSyncFilters ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
                    <div className="spinner" style={{ margin: '0 auto 10px' }}></div>
                    Loading settings...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ background: '#fff7ed' }}>
                            <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#c2410c', borderBottom: '2px solid #f97316' }}>Setting</th>
                            <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#c2410c', borderBottom: '2px solid #f97316' }}>Description</th>
                            <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#c2410c', borderBottom: '2px solid #f97316', width: '120px' }}>Enabled</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            {
                              key: 'enable_order_sync',
                              label: 'Enable Order Sync',
                              description: 'Push orders received on the Merchant store to the Vendor store.',
                            },
                            {
                              key: 'push_only_if_order_paid',
                              label: 'Push Only If Order Paid',
                              description: 'Push only paid orders received on the Merchant store to the Vendor store.',
                            },
                            {
                              key: 'push_orders_for_unsynced_products',
                              label: 'Push Orders for Unsynced Products',
                              description: 'Push unmapped product orders received on the Merchant store to the Vendor store.',
                            },
                            {
                              key: 'override_customer_info_with_merchant_details',
                              label: 'Override Customer Info with Merchant Details',
                              description: 'Replace customer info with merchant details when pushing orders to vendor stores.',
                            },
                          ].map((item, idx) => (
                            <tr key={item.key} style={{ background: idx % 2 === 0 ? 'white' : '#fff7ed' }}>
                              <td style={{ padding: '14px 16px', borderBottom: '1px solid #fed7aa', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap' }}>
                                {item.label}
                              </td>
                              <td style={{ padding: '14px 16px', borderBottom: '1px solid #fed7aa', color: '#64748b', fontSize: '13px' }}>
                                {item.description}
                              </td>
                              <td style={{ padding: '14px 16px', borderBottom: '1px solid #fed7aa', textAlign: 'center' }}>
                                <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={!!syncFieldFilters[item.key]}
                                    onChange={() => autoSaveSyncFilters(item.key, !syncFieldFilters[item.key])}
                                    style={{ opacity: 0, width: 0, height: 0 }}
                                  />
                                  <span style={{
                                    position: 'absolute',
                                    cursor: 'pointer',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: syncFieldFilters[item.key] ? '#f97316' : '#cbd5e1',
                                    transition: '0.3s',
                                    borderRadius: '28px',
                                    boxShadow: syncFieldFilters[item.key] ? '0 0 8px rgba(249, 115, 22, 0.4)' : 'none'
                                  }}></span>
                                  <span style={{
                                    position: 'absolute',
                                    height: '22px',
                                    width: '22px',
                                    left: syncFieldFilters[item.key] ? '27px' : '3px',
                                    bottom: '3px',
                                    backgroundColor: 'white',
                                    transition: '0.3s',
                                    borderRadius: '50%',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                  }}></span>
                                </label>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {savingSyncFilters && (
                      <div style={{ padding: '12px', background: '#fff7ed', borderRadius: '8px', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                        <span style={{ fontSize: '13px', color: '#c2410c', fontWeight: '600' }}>Saving...</span>
                      </div>
                    )}
                  </div>
                )
              ) : loadingSyncFilters ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
                  <div className="spinner" style={{ margin: '0 auto 10px' }}></div>
                  Loading settings...
                </div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ background: '#fff7ed' }}>
                          <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#c2410c', borderBottom: '2px solid #f97316' }}>Field Name</th>
                          <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#c2410c', borderBottom: '2px solid #f97316', width: '150px' }}>To be Sync</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((field, idx) => (
                          <tr key={field.key} style={{ background: idx % 2 === 0 ? 'white' : '#fff7ed' }}>
                            <td style={{ padding: '14px 16px', borderBottom: '1px solid #fed7aa' }}>
                              <div>
                                <span style={{ fontWeight: '600', color: '#1e293b' }}>{field.label}</span>
                                {field.helperText && (
                                  <div style={{
                                    fontSize: '12px',
                                    color: '#64748b',
                                    marginTop: '4px',
                                    lineHeight: '1.4'
                                  }}>
                                    {field.helperText}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '14px 16px', borderBottom: '1px solid #fed7aa', textAlign: 'center' }}>
                              <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={!!syncFieldFilters[field.key]}
                                  onChange={() => handleToggle(field.key)}
                                  style={{ opacity: 0, width: 0, height: 0 }}
                                />
                                <span style={{
                                  position: 'absolute',
                                  cursor: 'pointer',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  backgroundColor: syncFieldFilters[field.key] ? '#f97316' : '#cbd5e1',
                                  transition: '0.3s',
                                  borderRadius: '28px',
                                  boxShadow: syncFieldFilters[field.key] ? '0 0 8px rgba(249, 115, 22, 0.4)' : 'none'
                                }}></span>
                                <span style={{
                                  position: 'absolute',
                                  content: '""',
                                  height: '22px',
                                  width: '22px',
                                  left: syncFieldFilters[field.key] ? '27px' : '3px',
                                  bottom: '3px',
                                  backgroundColor: 'white',
                                  transition: '0.3s',
                                  borderRadius: '50%',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }}></span>
                              </label>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Saving indicator */}
                  {savingSyncFilters && (
                    <div style={{ marginTop: '16px', padding: '12px', background: '#fff7ed', borderRadius: '8px', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                      <span style={{ fontSize: '13px', color: '#c2410c', fontWeight: '600' }}>Saving...</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Format currency
  const formatCurrency = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(price);
  };

  // Get status badge class for the product's catalog status (terminal_status:
  // draft/active/unlisted/archived/deleted — the API never returns a bare `status`).
  const getStatusClass = (terminalStatus) => {
    switch (terminalStatus) {
      case 'draft':
        return 'statusDraft';
      case 'active':
        return 'statusActive';
      case 'unlisted':
        return 'statusInactive';
      case 'archived':
        return 'statusInactive';
      case 'deleted':
        return 'statusDeleted';
      default:
        return 'statusDefault';
    }
  };

  // Render products tab
  const renderProducts = () => {
    const totalCount = productsServerPagination?.total_count || products.length;
    const totalPages = productsServerPagination?.total_pages || 1;
    const startIndex = productsServerPagination ? (productsCurrentPage - 1) * productsItemsPerPage + 1 : 1;
    const endIndex = productsServerPagination
      ? Math.min(productsCurrentPage * productsItemsPerPage, totalCount)
      : products.length;

    return (
      <div className="vendorProductsTab">
        <div className="vendorProductsHeader">
          <div className="vendorProductsSearch">
            <FaSearch className="searchIcon" />
            <input
              type="text"
              placeholder="Search products..."
              value={productsSearchTerm}
              onChange={(e) => setProductsSearchTerm(e.target.value)}
              className="vendorProductsSearchInput"
            />
          </div>
          <button
            type="button"
            onClick={() => navigate('/suspire-terminal/products/add', {
              state: { vendorId: vendor.id },
            })}
            className="addProductButton"
          >
            <FaPlus />
            Add Product
          </button>
        </div>

        {productsLoading && products.length === 0 ? (
          <div className="loadingContainer">
            <p>Loading products...</p>
          </div>
        ) : productsError && products.length === 0 ? (
          <DataFallback
            variant="table"
            error={typeof productsError === 'string'
              ? { message: productsError, retryable: true }
              : productsError}
            onRetry={handleRetryProducts}
          />
        ) : products.length === 0 ? (
          <p className="noDataText">No products found.</p>
        ) : (
          <>
            <div className="resultsSummary">
              Showing {startIndex}-{endIndex} of {totalCount} products
            </div>

            <div className="vendorTableWrapper">
              <table className="vendorTable">
                <thead>
                  <tr>
                    <th>Product ID</th>
                    <th>Title</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const firstVariant = product.variants?.[0];
                    const price = firstVariant?.price || 0;
                    return (
                      <tr key={product.id}>
                        <td>{product.id.substring(0, 8)}...</td>
                        <td>
                          <span
                            className="clickableVendorName"
                            onClick={() => navigate(`/suspire-terminal/products/manage/${product.id}`)}
                          >
                            <span className="vendorName">{product.title || 'N/A'}</span>
                          </span>
                        </td>
                        <td>{firstVariant?.sku || 'N/A'}</td>
                        <td>{product.category?.name || 'N/A'}</td>
                        <td>{formatCurrency(price)}</td>
                        <td>
                          <span className={`statusBadge ${getStatusClass(product.terminal_status)}`}>
                            {product.terminal_status
                              ? product.terminal_status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                              : 'N/A'}
                          </span>
                        </td>
                        <td>
                          {product.created_at
                            ? new Date(product.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                            : 'N/A'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={productsCurrentPage}
                totalPages={totalPages}
                onPageChange={setProductsCurrentPage}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'company-details':
        return renderCompanyDetails();
      case 'contact-details':
        return renderContactDetails();
      case 'billing-invoice':
        return renderBillingInvoice();
      case 'warehouses':
        return renderWarehouses();
      case 'products':
        return renderProducts();
      case 'shipping-partners':
        return renderShippingPartners();
      case 'users-permissions':
        return renderUsersPermissions();
      case 'payment-integration':
        return renderPaymentIntegration();
      case 'custom-settings':
        return renderCustomSettings();
      case 'sync-settings':
      case 'product-sync':
        return renderSyncSettings();
      case 'connect-shopify':
        return renderConnectShopify();
      default:
        return renderCompanyDetails();
    }
  };

  // Get products count and commission from vendor data
  const productsCount = vendor.products_count || 0;
  const commission = vendor.commission || '0% + Rs. 0.00';

  // Get the active tab label
  const activeTabData = tabs.find(tab => tab.id === activeTab);
  const activeTabLabel = activeTabData?.label || 'Vendor Profile';
  const activeTabIcon = activeTabData?.icon || '👤';

  return (
    <div className="vendorDetailView">
      <div className="detailViewHeader">
        <div className="headerLeft">
          <div className="vendorProfileTitleSection">
            <div className="vendorProfileIcon">
              <span className="profileIconSymbol">{activeTabIcon}</span>
            </div>
            <h1 className="vendorProfileTitle">{activeTabLabel}</h1>
            <button className="helpIconBtn" title="Help">
              <FaQuestionCircle />
            </button>
          </div>
        </div>
        <div className="headerRight">
          <button
            type="button"
            onClick={onClose}
            className="closeButton"
          >
            ×
          </button>
        </div>
      </div>

      <div className="detailViewTabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tabButton ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tabIcon">{tab.icon}</span>
            <span className="tabLabel">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="detailViewContent">
        {renderTabContent()}
      </div>

      {/* Remove Product Sync Confirmation Modal */}
      {showRemoveSyncModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => !removingSync && setShowRemoveSyncModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1e293b',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '24px' }}>⚠️</span>
              Remove Product Update Sync?
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#64748b',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              This will stop syncing product updates from this vendor's Shopify store.
              The products will remain live and visible, but any future changes made in Shopify
              will no longer be automatically synced to the vendor store.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowRemoveSyncModal(false)}
                disabled={removingSync}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  color: '#64748b',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: removingSync ? 'not-allowed' : 'pointer',
                  opacity: removingSync ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveProductSync}
                disabled={removingSync}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: removingSync ? '#94a3b8' : '#dc2626',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: removingSync ? 'not-allowed' : 'pointer',
                  opacity: removingSync ? 0.7 : 1
                }}
              >
                {removingSync ? 'Removing...' : 'Confirm / Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Webhook Confirmation Modal */}
      {showRemoveWebhookModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => !removingWebhook && setShowRemoveWebhookModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1e293b',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '24px' }}>⚠️</span>
              {webhookTypeToRemove === 'product' ? 'Remove Product Update Webhook?' : 'Remove Order Update Webhook?'}
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#64748b',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              {webhookTypeToRemove === 'product'
                ? 'This will stop syncing product updates from your Shopify store to Suspire. Your existing products will remain live. You can reconnect at any time by clicking Update Connection.'
                : 'This will stop syncing order updates from your Shopify store to Suspire. Your existing orders will remain in the system. You can reconnect at any time by clicking Update Connection.'
              }
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowRemoveWebhookModal(false);
                  setWebhookTypeToRemove(null);
                }}
                disabled={removingWebhook}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  color: '#64748b',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: removingWebhook ? 'not-allowed' : 'pointer',
                  opacity: removingWebhook ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveWebhook}
                disabled={removingWebhook}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: removingWebhook ? '#94a3b8' : '#dc2626',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: removingWebhook ? 'not-allowed' : 'pointer',
                  opacity: removingWebhook ? 0.7 : 1
                }}
              >
                {removingWebhook ? 'Removing...' : 'Yes, Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Rich Text Editor Component
function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(value || '');

  useEffect(() => {
    if (editorRef.current) {
      if (value !== internalValue && !isFocused) {
        editorRef.current.innerHTML = value || '';
        setInternalValue(value || '');
      } else if (!editorRef.current.innerHTML && value) {
        editorRef.current.innerHTML = value;
        setInternalValue(value);
      }
    }
  }, [value, isFocused, internalValue]);

  const handleInput = (e) => {
    const content = e.target.innerHTML;
    setInternalValue(content);
    onChange(content);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    // Update value after command execution
    setTimeout(() => {
      if (editorRef.current) {
        const content = editorRef.current.innerHTML;
        setInternalValue(content);
        onChange(content);
      }
    }, 0);
  };

  const handleImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const handleLink = () => {
    const url = prompt('Enter link URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  return (
    <div className="richTextEditorWrapper">
      <div className="richTextToolbar">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          title="Bold"
          className="toolbarBtn"
        >
          <FaBold />
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          title="Italic"
          className="toolbarBtn"
        >
          <FaItalic />
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          title="Underline"
          className="toolbarBtn"
        >
          <FaUnderline />
        </button>
        <button
          type="button"
          onClick={() => execCommand('strikeThrough')}
          title="Strikethrough"
          className="toolbarBtn"
        >
          <FaStrikethrough />
        </button>
        <div className="toolbarSeparator" />
        <button
          type="button"
          onClick={() => execCommand('justifyLeft')}
          title="Align Left"
          className="toolbarBtn"
        >
          <FaAlignLeft />
        </button>
        <button
          type="button"
          onClick={() => execCommand('justifyCenter')}
          title="Align Center"
          className="toolbarBtn"
        >
          <FaAlignCenter />
        </button>
        <button
          type="button"
          onClick={() => execCommand('justifyRight')}
          title="Align Right"
          className="toolbarBtn"
        >
          <FaAlignRight />
        </button>
        <div className="toolbarSeparator" />
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          title="Bullet List"
          className="toolbarBtn"
        >
          <FaListUl />
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          title="Numbered List"
          className="toolbarBtn"
        >
          <FaListOl />
        </button>
        <div className="toolbarSeparator" />
        <button
          type="button"
          onClick={handleImage}
          title="Insert Image"
          className="toolbarBtn"
        >
          <FaImage />
        </button>
        <button
          type="button"
          onClick={handleLink}
          title="Insert Link"
          className="toolbarBtn"
        >
          <FaLink />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className={`richTextEditorContent ${isFocused ? 'focused' : ''}`}
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        suppressContentEditableWarning
        data-placeholder={placeholder}
      />
    </div>
  );
}

export default ManageVendors;
export { VendorDetailView };

